import { AfterViewChecked, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { BarVertical2DComponent, BarVerticalStackedComponent, Color, LineChartComponent, ScaleType } from '@swimlane/ngx-charts';
import { UserMenuService } from '@api/user-menu.service';
import { curveMonotoneX } from 'd3-shape';
import { MeasuredDemandVM, ResourceSeriesResponse, ResourceSeriesVM, SeriesAggregation, SiteMicVM, SolarConfigVM, TariffRateVM } from '@models/backend-generated-models/models';
import { BatteryHour } from './battery-sim';
import { download, fileName, printImage, renderChartPng, seriesToCsv } from './chart-export';
import { applyBands, SeriesBand, StackedPoint } from './series-bands';
import { ChartGuide } from './chart-guides';
import { isGenerationName, pickGrid, pickMains, sumSeries, withColours } from './series-util';
import { WhatIfScenario } from './what-if-panel.component';
import { formatNumber, formatValue, formatWithUnit, ReportKind, reportKind } from './format';
import { bucketKey, bucketTitle, fillGaps, formatDate, keyDate, nextPeriodStart, Period, stamp } from './periods';
import { bucketAt, GeoBucket, plotGeometry, PlotGeometry } from './plot-geometry';
import { BandOut, GuideOut, LabelOut, LeaderOut, layoutBands, layoutGuides, layoutLabels } from './overlay-layout';
import { computeTotals, ResourceTotals } from './totals';

/** One step down the drill path: the unit landed on, and the span it covers. */
interface DrillStep {
  level: 'm' | 'w' | 'd' | 'h';
  from: Date;
  /** Exclusive. */
  to: Date;
  label: string;
}

/** One group's figure in each Compare period, and the change between them. */
interface CompareRow {
  name: string;
  rgb: string;
  left: number;
  right: number;
  change: number | null;
  total: boolean;
}

/** Everything Compare draws, worked out once per response. */
interface CompareView {
  periods: string[];
  rows: CompareRow[];
  pies: { name: string; value: number }[][];
  colours: { name: string; value: string }[];
  legends: { name: string; rgb: string; value: string }[][];
}

/** The numbers the tiles show, worked out in totals.ts. Re-exported for the what-if. */
export type { ResourceTotals } from './totals';

@Component({
  selector: 'app-resource-series-chart',
  templateUrl: './resource-series-chart.component.html',
  styleUrls: ['./resource-series-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResourceSeriesChartComponent implements OnChanges, AfterViewInit, AfterViewChecked, OnDestroy {

  @Input() siteId!: number;
  @Input() readingTypeId!: number;
  @Input() period: string = '7d';
  @Input() dateLeft!: string;
  @Input() dateRight!: string;

  /**
   * The category's own report, which decides the proc behind /resource-series. Leaving it off gets
   * the remainder report, which is all the endpoint served before it covered the policy and
   * consumption ones, so an unset value behaves exactly as this component did before.
   */
  @Input() reportUrl?: string;

  /**
   * The selected group's own parameter name, straight off GroupVM.Type - "aLogicalGroupID",
   * "aPolicyID" or "aPolicyGroupID" - passed through rather than re-derived, exactly as the SSRS
   * URL passes it. This is the mechanism that makes the legacy chart work for any category an admin
   * sets up, and copying it is what lets this one do the same.
   */
  @Input() groupType?: string;
  @Input() groupId?: number;

  /**
   * Which logical group is the grid connection. Optional: when it is not given we take the series
   * that actually goes negative, because only a grid meter can export. If nothing goes negative the
   * split cannot be inferred and the import/export tiles are hidden rather than guessed - the old
   * SSRS cards guessed by matching "main"/"solar" in the group NAME, which breaks on any site whose
   * groups are named differently.
   */
  @Input() gridLogicalGroupId?: number;

  /**
   * The what-if panel, handed to whoever wants to place it. The dialog renders it in its own column
   * beside the whole modal rather than inside this chart, so the panel is not boxed in by the
   * chart's height - a battery table did not fit in there.
   *
   * Bindings inside it still resolve against THIS component, so all the state stays here.
   */
  @ViewChild('whatIfTpl', { static: true }) whatIfTpl!: TemplateRef<unknown>;

  /**
   * Fired whenever something the panel shows has changed off the back of an async result. The host
   * view holds the panel, and markForCheck on our own detector does not reach it.
   */
  @Output() stateChanged = new EventEmitter<void>();

  isLoading = false;
  errorMessage: string | null = null;

  chartData: StackedPoint[] = [];
  colours: Color = { name: 'resource', selectable: true, group: ScaleType.Ordinal, domain: [] };
  totals: ResourceTotals | null = null;

  /** From ReadingTypes.Unit, never assumed - this chart also renders m3, Litre, kg of CO2. */
  unit = '';

  /** The reading type's name, "Active Energy", for the axis title. */
  readingTypeName = '';

  /**
   * The value axis title, as SSRS wrote it: "Active Energy, kWh". A policy chart's figures are in the
   * policy's unit, not the reading type's, so it says the unit alone, and that its policies differ
   * where they do.
   */
  get axisTitle(): string {
    if (this.rawSeries.some(s => s.unit)) return 'Mixed units';
    const kind = this.reportKind;
    if (kind === 'policy' || kind === 'breakdown') return this.unit;
    return [this.readingTypeName, this.unit].filter(Boolean).join(', ');
  }

  /** A share of a whole means nothing once a figure in it is negative, or is an average. */
  get showShares(): boolean {
    return !this.averaged && !(this.totals?.perSeries ?? []).some(s => s.value < 0);
  }

  /** The month(s) the bars fall in, shown once under the axis the way the SSRS report does. */
  periodLabel = '';

  // ---- What-if: scale one series and let the grid absorb the difference --------------------------

  /** Untouched response, kept so every multiplier is applied to the original numbers. */
  rawSeries: ResourceSeriesVM[] = [];


  /** Which group is being scaled - chosen, not guessed. Null until a response arrives. */
  scaleGroupId: number | null = null;

  /** Populated from the response so the picker names real groups. */
  scaleOptions: { id: number | null; name: string }[] = [];

  /** Name and colour per group, in the order they are stacked. Drives the legend. */
  legendItems: { name: string; rgb: string }[] = [];

  // ---- Many meters --------------------------------------------------------------------------
  //
  // Aran Woolen Mills (14 sub-meters) and Killeshal Unit 3 (18) on production, 02/10/26: a tile per
  // meter took two or three rows and squeezed the plot to a strip, the legend wrapped over the dates,
  // and a number per segment piled up where no one could tell which was which. Past a handful of
  // meters the tiles and the legend become one list beside the plot, and a column's figures are
  // read off a hover panel rather than off the bars.

  /** More meters than this and the tiles and legend become the meter list. */
  static readonly MANY_SERIES = 6;

  /** At least this many in a stack and its segments say only what fits, with the column's total on top. */
  static readonly BUSY_STACK = 3;

  /**
   * Meters with readings. Not the legend's length: a what-if adds bands (battery, export) to the
   * legend, and a two-meter Mains/Solar PV chart with a battery is still a two-meter chart.
   */
  get meterCount(): number {
    return this.rawSeries.filter(s => s.points?.length).length;
  }

  /**
   * The tiles and legend are one meter list beside the plot: past a handful of meters, and on every
   * Combined chart (the remainder report's stack of sub meters and what is left of the mains),
   * where each meter's share of the whole is the point (Jayden, 16/10/26: Wilton had the list with
   * 7 meters and Naas, with 6, did not). Not a Mains/Solar PV pair, whose import, export and solar
   * tiles say more than a list of two. Never in Compare, which has its own legends.
   */
  get meterList(): boolean {
    if (this.compare) return false;
    if (this.meterCount > ResourceSeriesChartComponent.MANY_SERIES) return true;
    return this.reportKind === 'remainder' && this.chartStyle === 'stacked' && this.meterCount > 1
      && this.baseTotals?.grossImport == null;
  }

  /**
   * Headroom over the tallest column of a busy stack, for its total. Without it the total of the
   * tallest column had nowhere to go but onto its own top segment, over that segment's number.
   */
  get yScaleMax(): number | undefined {
    if (this.chartStyle !== 'stacked' || this.meterCount < ResourceSeriesChartComponent.BUSY_STACK) return undefined;
    const tallest = Math.max(0, ...this.chartData.map(b => b.series.reduce((a, s) => a + Math.max(0, s.value), 0)));
    return tallest > 0 ? tallest * 1.1 : undefined;
  }

  /** One panel per column, listing every meter, in place of ngx-charts' one-segment tooltip. */
  get columnTips(): boolean {
    return !this.compare && this.chartStyle !== 'line'
      && this.meterCount >= ResourceSeriesChartComponent.BUSY_STACK;
  }

  /** The meter list: each meter's total and share, biggest first. */
  get meterRows(): { name: string; rgb: string; value: number | null; share: number | null }[] {
    if (this.meterRowsFor === this.totals && this.meterRowsItems === this.legendItems) return this.meterRowsCache;
    const byName = new Map((this.totals?.perSeries ?? []).map(s => [s.name, s.value]));
    const total = this.totals && !this.averaged ? this.totals.perSeries.reduce((a, s) => a + s.value, 0) : 0;
    this.meterRowsCache = this.legendItems
      .map(l => {
        const value = byName.has(l.name) ? byName.get(l.name)! : null;
        return { name: l.name, rgb: l.rgb, value, share: value !== null && total ? value / total : null };
      })
      .sort((a, b) => Math.abs(b.value ?? 0) - Math.abs(a.value ?? 0));
    this.meterRowsFor = this.totals;
    this.meterRowsItems = this.legendItems;
    return this.meterRowsCache;
  }
  private meterRowsFor: ResourceTotals | null | undefined = undefined;
  private meterRowsItems: { name: string; rgb: string }[] | null = null;
  private meterRowsCache: { name: string; rgb: string; value: number | null; share: number | null }[] = [];

  byName = (_: number, row: { name: string }) => row.name;

  /**
   * The meter whose row in the list is hovered, and ngx-charts' own entries for its bars. ngx-charts
   * marks those bars active and the stylesheet fades the rest (.rsc-highlighting). It used to find the
   * bars by their fill colour, which lit two meters at once wherever the palette repeated a colour.
   */
  highlighted: string | null = null;
  activeEntries: { name: string; value: number }[] = [];

  /** Fade every meter but one in the plot, while that meter's row in the list is hovered. */
  highlightSeries(name: string | null): void {
    this.highlighted = name;
    // SeriesVerticalComponent.isActive matches an entry on its name and value.
    const entries: { name: string; value: number }[] = [];
    if (name) for (const b of this.chartData) for (const s of b.series) if (s.name === name) entries.push({ name, value: s.value });
    this.activeEntries = entries;
    // Column totals stay: they are what the faded column still adds up to.
    this.host.nativeElement.querySelectorAll<HTMLElement>('.rsc-seg-label:not(.rsc-col-total)').forEach(l => {
      l.style.opacity = name && l.dataset['series'] !== name ? '0' : '';
    });
    this.cdr.markForCheck();
  }

  /**
   * Optional ceiling on a single interval's generation, in the chart's own unit. Without it a x2
   * on a 172 kWh hour claims 344 kWh out of a 200 kVA connection, which is not physically possible.
   * Only meaningful where one bar is one hour, so it stays unset until a per-site inverter rating
   * exists to read.
   */
  @Input() intervalCap?: number;


  /** The site's installed figures, or null where nobody has recorded them yet. */
  solarConfig: SolarConfigVM | null = null;

  /** The rates the client saved in the what-if, passed straight through to it. Empty where none. */
  tariffRates: TariffRateVM[] = [];

  /** The MIC the client entered in the what-if, or null where none is entered. */
  siteMic: SiteMicVM | null = null;

  /** Where the hourly response's estimated solar comes from (PVGIS for the site's location). */
  solarEstimateNote: string | null = null;

  /** kWh one kWp makes on each day of a typical year at the site, from PVGIS. See the what-if's seasonFactor. */
  solarYearByDay: number[] | null = null;

  /** The site's metered apparent power for this window, null where no meter records it. */
  measured: MeasuredDemandVM | null = null;

  /** Totals as metered, so the what-if can report what changed rather than only the new number. */
  baseTotals: ResourceTotals | null = null;


  /**
   * Off by default - the chart's job is to show what was metered.
   *
   * Two-way bound so the button can live in the dialog header beside the H and T tickboxes, where
   * every other chart-wide control already is, rather than in a strip of its own above the tiles.
   */
  @Input() showWhatIf = false;
  @Output() showWhatIfChange = new EventEmitter<boolean>();

  /**
   * Whether the what-if is offerable at all. The header button is the parent's to draw, so the
   * parent has to be told - it cannot see the series count from out there.
   */
  @Output() whatIfAvailable = new EventEmitter<boolean>();
  private lastAvailable: boolean | null = null;

  /**
   * The totals start folded behind their caret, on every chart, and open with a click (Jayden,
   * 12/10/26: with several groups on a chart they took far too much of its height). Folded, the
   * drill's breadcrumbs share their strip, so the plot gets both rows.
   */
  tilesCollapsed = true;

  /**
   * The least height the plot's svg is drawn at, axis included: a plot of about 150px. On a laptop
   * with the what-if open the plot was being handed whatever was left, 103px at 1366 x 768 and less
   * on a smaller screen (09/10/26). Below this the chart's side of the dialog scrolls rather than the
   * plot being crushed. Low enough that 1280 x 720 still fits with the tiles open and no scrollbar.
   */
  static readonly MIN_VIEW_H = 200;

  // ---- Hourly readings, fetched here only because the tiles still need them ----------------------

  /**
   * Hourly readings for the same window, fetched separately from the chart's own series.
   *
   * The endpoint buckets server-side by the period letter, so the displayed series is whatever the
   * user picked - monthly totals on a year view. A battery simulated on monthly totals is nonsense:
   * the entire value of a battery is the shape WITHIN a day, which a day or month total has already
   * averaged away. So the panel always asks for 'h' regardless of what the chart is showing.
   */
  hourlyRows: BatteryHour[] = [];
  hourlyLoaded = false;


  /**
   * Legend and stack label for units sold to the grid.
   *
   * ngx-charts colours by series name, so one grid series can only ever have one colour - and the
   * grid series carries BOTH import (positive) and export (negative). That made the same red mean
   * "bought from the grid" above the axis and "sold to the grid" below it, which reads as random
   * once a few bars go negative. Splitting export into its own series is the only way to give it
   * its own colour.
   *
   * The scheme this settles on: red only ever means bought, and the solar colour family only ever
   * means came-from-solar, deepened for what the battery kept and lightened for what was sold.
   */
  static readonly EXPORT_BAND = 'Exported to grid';


  // ---- measured apparent power ---------------------------------------------------------------
  //
  // MIC is reserved and billed in kVA. The panel used to compare a kW peak against that kVA limit,
  // which is not the same quantity: apparent power is larger, by one over the power factor. At a
  // site whose power factor sags under load the gap is big enough to reverse the answer. Lee Strand
  // reads 260 kW against a 500 kVA MIC and looks fine; its meter has recorded 519 kVA and is over.
  //
  // So where the site meters apparent power, every figure in this panel comes off that meter. Where
  // it does not, the old active-power behaviour stands and the panel says which it is using.


  /**
   * The dialog's H box: the numbers written inside the bars. Presentation only, so changing it
   * redraws the labels and never refetches (see ngOnChanges).
   */
  @Input() showLabels = true;

  /**
   * The dialog's Compare box: two periods side by side instead of a run of buckets. The periods
   * start at compareLeft and compareRight, each one `period` long, as the SSRS Compare reports
   * take them. The chart then draws the groups side by side, a pie for each period, and tiles that
   * give each period's figure and the change between them.
   */
  @Input() compare = false;
  @Input() compareLeft = '';
  @Input() compareRight = '';

  /** The dialog's T box: the export and print toolbar. Presentation only, like H. */
  @Input() showToolbar = false;

  /**
   * False while the dialog is still working out the window, the latest-reading lookup on opening
   * or after a change of meter. The chart stays on screen and loads nothing until it is true, then
   * loads once with whatever changed in the meantime.
   *
   * The dialog used to take the chart off the page for that lookup instead, so the box collapsed
   * and grew back on opening and on every change of meter.
   */
  @Input() ready = true;

  /** Anything on its way: the first load, a reload, a drill, or the dialog still choosing the window. */
  get busy(): boolean {
    return this.isLoading || this.drillLoading || !this.ready;
  }

  /** What the exports are called and titled, e.g. "Mains/Solar PV: The Mains". */
  @Input() chartTitle = '';

  /** Set while an image is being drawn, or when a popup blocker stopped Print. */
  exportMessage: string | null = null;

  /** What is on screen: the scenario when one is applied, the readings otherwise. */
  private get shownSeries(): ResourceSeriesVM[] {
    return this.liveScenarioSeries ?? this.rawSeries;
  }

  /**
   * What a what-if has changed, or null when the chart shows readings.
   *
   * An export leaves the page, and a file of simulated figures that does not say so reads as the
   * site's real consumption once it is in a spreadsheet or someone's inbox. So every export made
   * under a scenario is labelled, in the file and in its name, with the what-if's own wording.
   */
  private get simulatedNote(): string | null {
    const active = this.liveScenarioSeries !== null || this.liveMultiplier !== 1
      || this.liveCap !== null || this.liveScenarioBands.length > 0;
    return active ? (this.scenarioNote || 'what-if scenario') : null;
  }

  /** The window as a file name suffix, following the drill, marked when simulated. */
  private get exportName(): string {
    const top = this.drillStack[this.drillStack.length - 1];
    const crumb = top ? ' ' + top.label : '';
    const simulated = this.simulatedNote ? ' (simulated)' : '';
    return `${this.chartTitle || 'Chart'}${crumb}${simulated}`;
  }

  /** The picture's title: the name, plus what was simulated. */
  private get exportTitle(): string {
    const note = this.simulatedNote;
    const top = this.drillStack[this.drillStack.length - 1];
    const base = `${this.chartTitle || 'Chart'}${top ? ' ' + top.label : ''}`;
    return note ? `${base} (simulated: ${note})` : base;
  }

  exportCsv(): void {
    const csv = seriesToCsv(this.shownSeries, this.unit, this.periodKey === 'h', this.averaged, this.simulatedNote);
    download(new Blob([csv], { type: 'text/csv;charset=utf-8' }), fileName(this.exportName, 'csv'));
  }

  async exportPng(): Promise<void> {
    const png = await this.drawPng();
    if (png) download(png, fileName(this.exportName, 'png'));
  }

  async printChart(): Promise<void> {
    const png = await this.drawPng();
    if (png && !(await printImage(png, this.exportName))) {
      this.exportMessage = 'Your browser blocked the print window. Allow popups for this site and try again.';
      this.touch();
    }
  }

  private async drawPng(): Promise<Blob | null> {
    // The whole row when the meter list stands in for the legend, so the picture still says which
    // colour is which.
    const box = this.host.nativeElement.querySelector<HTMLElement>(this.meterList ? '.rsc-plot-row' : '.rsc-chart');
    if (!box) return null;

    this.exportMessage = null;
    // The meter list scrolls on screen, and a picture of it showed only the rows in view: unfolded for
    // the moment the picture is taken (.rsc-exporting), then put back.
    // The plot keeps its size while the row grows round the list, or it would redraw mid-picture.
    const plot = box.querySelector<HTMLElement>('.rsc-chart');
    const plotHeight = plot?.style.height ?? '';
    if (plot && this.meterList) plot.style.height = plot.offsetHeight + 'px';
    box.classList.add('rsc-exporting');
    try {
      await new Promise(r => requestAnimationFrame(r));
      return await renderChartPng(box, this.exportTitle);
    } catch {
      this.exportMessage = 'The chart could not be turned into an image.';
      this.touch();
      return null;
    } finally {
      box.classList.remove('rsc-exporting');
      if (plot) plot.style.height = plotHeight;
    }
  }

  /**
   * Any chart of electricity that covers the site's mains, with solar or without (Jayden, 08/10/26).
   * It used to be one ticked category, Mains/Solar PV, with FPCategories.AllowWhatIf. Not a policy
   * chart (its figures are CO2 or money, not the meter) and not Compare.
   */
  get canWhatIf(): boolean {
    // kWh, or no unit at all: some electricity reading types have none recorded (PCL's "Energy, kWh"
    // on dev). The mains test still has to pass, and it turns away gas, water and oil by name.
    return !this.compare && /^\s*(kwh)?\s*$/i.test(this.unit || '') && !!this.mains(this.rawSeries);
  }

  get scaleName(): string {
    return this.scaleOptions.find(o => o.id === this.scaleGroupId)?.name ?? '';
  }

  /** Closing from inside the panel has to tell the header button, which owns the state. */
  closeWhatIf(): void {
    this.showWhatIf = false;
    this.showWhatIfChange.emit(false);
    this.clearScenario();
    // The chart box changes width, so the labels have to be repositioned.
    this.scheduleOverlays();
  }

  /** Mark ourselves dirty AND tell the host, which may be rendering the what-if panel. */
  private touch(): void {
    this.cdr.markForCheck();
    this.stateChanged.emit();
  }

  toggleTiles(): void {
    this.tilesCollapsed = !this.tilesCollapsed;
    this.scheduleOverlays();
  }

  /**
   * Tell the parent whether the header button should exist. Emitted outside change detection's
   * current pass, because it is derived from data that arrives mid-render.
   */
  private publishAvailability(): void {
    const available = this.canWhatIf;
    if (available === this.lastAvailable) return;
    this.lastAvailable = available;
    Promise.resolve().then(() => this.whatIfAvailable.emit(available));
  }


  // A one-line verdict used to sit on the collapsed header. Removed on Jayden's call 11/09/26:
  // "over by 19 kVA" beside a date range of Nov 2024 to Sep 2026 reads as though the site is over
  // its connection for the whole window, when it is one hour in two years. The figure is honest
  // inside the block, where the peak's own timestamp is beside it, and misleading on its own.

  // ---- Info tooltips -------------------------------------------------------------------------


  // ---- Drill-down -----------------------------------------------------------------------------
  //
  // Donal's #1 ask off the 11/09/26 demo: he tried clicking into a day, repeatedly, and nothing
  // happened. A day bar shows one number - it has already thrown away the shape WITHIN the day,
  // which is exactly what he wants to see for solar ("is this a clean bell curve or is something
  // clipping it").
  //
  // 22/09/26: each step now ASKS THE SERVER for the narrowed window, where it used to re-bucket
  // `hourlyRows` in the browser. Two reasons, and the second is the one that matters.
  //
  // Speed. Drilling narrows the window, so a step is a small query. Measured on dev against the
  // real endpoint: a year of hours, which is what was being fetched up front on every chart load to
  // make drilling instant, is 863ms and 16,634 points; a drill into a month is 201ms, into a day
  // 167ms. The old shape charged every reader for a fetch only a drilling user needed.
  //
  // Correctness. The client-side version took the grid series and "the other one", so it only ever
  // worked on a two-series chart. This endpoint now serves 287 of the 301 categories, most of which
  // have neither two series nor a grid. Asking the server returns however many the category has,
  // with its own names and colours, and the drill stops being a mains-and-solar feature.

  /**
   * How far the chart has been drilled in, one entry per step down.
   *
   * The drill happens IN PLACE on the main chart rather than in a panel underneath it. A second
   * chart below the first halved the height of both and left you comparing two things that were
   * never meant to be compared; what is actually wanted is to keep looking at the same chart, at
   * the same size, further in.
   *
   * The path is strictly chronological, each step landing on the next unit down:
   *
   *     year  ->  months of that year
   *     month ->  weeks of that month
   *     week  ->  days of that week
   *     day   ->  hours of that day
   *
   * Empty means the server's own view, exactly as before.
   */
  drillStack: DrillStep[] = [];

  /**
   * The server's series, stashed on the way down so stepping back out restores it untouched.
   *
   * Every level below it is fetched from the server for just the bar that was clicked.
   */
  private baseSeries: ResourceSeriesVM[] | null = null;

  /**
   * How fine the readings on screen are, from the server: "h", "d" or "m". The drill stops there,
   * so a static month drills to its days, where the one reading sits on the 1st, and no further.
   * Null, from an older API or a window with no readings, drills as far as hours as it always did.
   * Follows the drill, because each drilled window is asked about separately.
   */
  resolution: string | null = null;
  private baseResolution: string | null = null;

  /** Set when a bar was clicked but there is nothing underneath it to show. */
  drillUnavailableReason: string | null = null;

  /** Whether clicking a bar does anything at this level. The data's own resolution is the floor. */
  get canDrill(): boolean {
    // Compare is two bars, one per period. There is nothing under either to drill into.
    return !this.compare && this.nextLevel(this.periodKey) !== null;
  }

  /** A drill request is in flight. The chart keeps the level it is on until the next one lands. */
  drillLoading = false;

  /** Breadcrumb: "All" plus one crumb per step down, each clickable to go back to it. */
  get drillCrumbs(): { label: string; depth: number }[] {
    // Rebuilt only when the path changes: a new array every pass recreated the buttons on every
    // mouse move, and a crumb focused from the keyboard lost its focus.
    const key = this.drillStack.map(s => s.label).join('|');
    if (key !== this.crumbsKey) {
      this.crumbsKey = key;
      this.crumbs = [{ label: 'All', depth: 0 }, ...this.drillStack.map((s, i) => ({ label: s.label, depth: i + 1 }))];
    }
    return this.crumbs;
  }
  private crumbsKey: string | null = null;
  private crumbs: { label: string; depth: number }[] = [];

  /** What one bar means right now, for the hint under the chart. */
  get drillNextLabel(): string | null {
    const next = this.compare ? null : this.nextLevel(this.periodKey);
    if (!next) return null;
    return { m: 'months', w: 'weeks', d: 'days', h: 'hours' }[next] ?? null;
  }

  /**
   * The unit one step below the given bucket, or null when the data has nothing finer to show.
   *
   *   hourly data   year > months > weeks > days > hours
   *   daily data    year > months > weeks > days
   *   monthly data  year > months > days     (weeks would be an empty extra click)
   */
  private nextLevel(level: string): 'm' | 'w' | 'd' | 'h' | null {
    const monthly = this.resolution === 'm';
    const hourly = this.resolution === 'h' || this.resolution === null;

    let next: 'm' | 'w' | 'd' | 'h' | null = null;
    if (level === 'y') next = 'm';
    else if (level === 'q') next = 'm';
    else if (level === 'm') next = monthly ? 'd' : 'w';
    else if (level === 'w') next = 'd';
    else if (level === 'd') next = hourly ? 'h' : null;

    // A policy is calculated over its own interval and means nothing finer: a monthly standing
    // charge spread over hours. rptResourcePolicyBreakdown stopped there (CalculatePolicyDDPeriod).
    const floor = this.policyFloor;
    if (next && floor && ResourceSeriesChartComponent.FINENESS[next] < ResourceSeriesChartComponent.FINENESS[floor]) return null;
    return next;
  }

  private static readonly FINENESS: Record<string, number> = { h: 0, d: 1, w: 2, m: 3, q: 4, y: 5 };

  /** The coarsest interval any policy on the chart is calculated over, or null on any other chart. */
  private get policyFloor(): Period | null {
    const letter: Record<string, Period> = { hour: 'h', day: 'd', week: 'w', bimonth: 'm', month: 'm', quarter: 'q', halfyear: 'y', year: 'y' };
    let floor: Period | null = null;
    for (const s of this.rawSeries) {
      const p = letter[(s.interval || '').trim().toLowerCase()];
      if (p && (!floor || ResourceSeriesChartComponent.FINENESS[p] > ResourceSeriesChartComponent.FINENESS[floor])) floor = p;
    }
    return floor;
  }

  /**
   * Wired to the stacked chart's (select). ngx-charts sets `data.series` to the clicked bar's own
   * group name when the click comes from inside a stack - which `toStacked`/`dateKey` set to the
   * bucket's "YYYY-MM-DD" key, so it parses straight back into the calendar day that was clicked.
   */
  /**
   * Wired to the chart's (select). ngx-charts sets `data.series` to the clicked bar's own group
   * name, which is the bucket key - so the bar is found by matching that key back against what was
   * rendered rather than by parsing it, because the key's format changes with the level.
   */
  onBarSelect(event: any): void {
    // The bars under "Loading..." are the previous chart's, so a click there means nothing.
    if (this.compare || this.busy) return;
    const next = this.nextLevel(this.periodKey);
    if (!next) return;

    // Stacked and grouped bars carry the bucket key in `series`; a line point carries its date in
    // `name` (the line's x axis is time). Whichever one matches a bucket is the one clicked.
    const keys = [event?.series, event?.name].map(k => k instanceof Date ? bucketKey(k, this.periodKey) : String(k ?? ''));
    const i = this.chartData.findIndex(b => keys.includes(b.name));
    if (i < 0) return;

    const start = this.bucketDates[i];
    if (!start) return;

    // Exactly the clicked bar's own period. It used to end where the next DRAWN bar started, and the
    // procs return no row for an empty bucket, so the day before a gap drilled into the whole gap: a
    // Saturday before three empty days opened 96 hours. Before that it ran to the end of the window,
    // which froze the page (80faf3e, 24/09/26).
    let from = start;
    let to = nextPeriodStart(start, this.periodKey);

    // And never outside the bar this level was drilled from, or at the top, outside the chart's own
    // window. A week bar in a month view starts on its Monday, which can be in the month before, but
    // only counted the days inside the month, so drilling into it must show those days and no others.
    const top = this.drillStack[this.drillStack.length - 1];
    const floor = top ? top.from : this.windowStart();
    const ceiling = top ? top.to : this.windowEnd();
    if (floor && from < floor) from = floor;
    if (to > ceiling) to = ceiling;
    if (to <= from) return;

    this.stepInto(next, from, to);
  }

  /** The start of the chart's own window, or null when it is not set. */
  private windowStart(): Date | null {
    const parsed = this.dateLeft ? new Date(this.dateLeft.replace(' ', 'T')) : null;
    return parsed && !isNaN(parsed.getTime()) ? parsed : null;
  }

  /**
   * The exclusive end of the last bar, which is the end of the window being charted.
   *
   * Read off the chart's own dateRight rather than the last hourly reading. The hourly rows exist
   * for the battery and are about to stop being fetched for every chart; the drill must not depend
   * on them, and the window the user set is the more honest bound anyway.
   */
  private windowEnd(): Date {
    // dateRight is the window's last second (23:59:59, as SSRS sent it), and this is exclusive.
    const parsed = this.dateRight ? new Date(this.dateRight.replace(' ', 'T')) : null;
    return parsed && !isNaN(parsed.getTime()) ? new Date(parsed.getTime() + 1000) : new Date(8640000000000000);
  }

  /** Step down one level into [from, to). */
  private stepInto(level: 'm' | 'w' | 'd' | 'h', from: Date, to: Date): void {
    this.fetchDrill(level, from, to, (series, resolution) => {
      // Stash the server's own view once, on the first step down.
      if (!this.drillStack.length) {
        this.baseSeries = this.rawSeries;
        this.baseResolution = this.resolution;
      }

      this.drillStack.push({ level, from, to, label: this.crumbLabel(level, from, to) });
      this.applyDrilled(series, resolution);
    });
  }

  /**
   * Ask the server for one window at one level, and hand the series to `apply` if it is still the
   * one being waited for.
   *
   * Cancellation is not optional here. The hang Jayden hit on the old design was not one slow
   * request, it was N of them outstanding at once because nothing dropped the previous one, each
   * landing and forcing a full re-render. Clicking down through a year is exactly that pattern, so
   * every drill request drops the one before it on the way in.
   */
  private fetchDrill(
    level: 'm' | 'w' | 'd' | 'h',
    from: Date,
    to: Date,
    apply: (series: ResourceSeriesVM[], resolution: string | null) => void): void {

    this.drillSub?.unsubscribe();
    this.drillSub = null;

    this.drillLoading = true;
    this.drillUnavailableReason = null;
    this.touch();

    // `to` is the next bar's start. A second is taken off it, as SSRS sent 23:59:59: the procs take
    // readings before the right bound, but the server's resolution check and gross tiles take it
    // inclusively, and a reading at exactly `to` belongs to the next bar.
    const right = new Date(to.getTime() - 1000);

    this.drillSub = this.userMenuService
      .getResourceSeries$(
        this.siteId,
        this.readingTypeId,
        // The same one letter code the dialog's own fetch sends (see nativePeriod).
        level,
        ResourceSeriesChartComponent.stamp(from),
        ResourceSeriesChartComponent.stamp(right),
        this.reportUrl,
        this.groupType,
        this.groupId)
      .subscribe({
        next: (response: ResourceSeriesResponse) => {
          this.drillLoading = false;

          const series = withColours(response?.series ?? []).filter(s => (s.points ?? []).length > 0);
          if (!series.length) {
            this.drillUnavailableReason = 'No readings inside that bar.';
            this.touch();
            return;
          }

          apply(series, response?.resolution ?? null);
        },
        error: () => {
          this.drillLoading = false;
          this.drillUnavailableReason = 'That view could not be loaded.';
          this.touch();
        }
      });
  }

  /** Put a drilled set of series on the chart. The drill stack is already where it should be. */
  private applyDrilled(series: ResourceSeriesVM[], resolution: string | null): void {
    this.rawSeries = series;
    this.resolution = resolution;
    this.drillUnavailableReason = null;
    this.dropStaleScenario();

    // The multiplier is deliberately left alone: drilling is a zoom, not a new scenario.
    this.spansMonths = this.countMonths(series) > 1;
    this.periodLabel = this.toPeriodLabel(series);
    this.render();
    this.touch();
  }


  /**
   * A drill swaps the series under an open what-if, and the scenario it last handed over was worked
   * out from the level just left. Drawn on, it put that level back over the new one: drilling a week
   * into Sunday with the what-if open showed the week's seven days again. Cleared exactly as a fresh
   * load clears it, and for the same reason: the panel recomputes off the new rawSeries and hands a
   * scenario back, and until it does, the chart draws the readings. The multiplier and cap stay, as
   * they did before, so the tiles are unchanged.
   */
  private dropStaleScenario(): void {
    this.scenarioSeries = null;
    this.scenarioBands = [];
    this.scenarioGuides = [];
    this.scenarioBasis = null;
  }

  /** The format the endpoint's dateLeft and dateRight take. Local time, never UTC. */
  private static stamp(d: Date): string {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ` +
           `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  }

  /**
   * Throw the drill path away without restoring anything.
   *
   * Used when a new site, reading type or date range arrives: whatever was drilled into may not even
   * exist in the new window, and a fresh response is about to replace rawSeries anyway, so the
   * stashed copy has to go with it rather than being put back over the top.
   */
  private resetDrill(): void {
    this.drillStack = [];
    this.baseSeries = null;
    this.baseResolution = null;
    this.drillUnavailableReason = null;
  }

  /** Back to a given depth. 0 is the server's own view. */
  drillToDepth(depth: number): void {
    if (depth >= this.drillStack.length) return;

    this.drillStack = this.drillStack.slice(0, depth);
    this.drillUnavailableReason = null;

    if (!this.drillStack.length) {
      // Stepping all the way back out needs no request: the server's own view was stashed on the
      // way down and is still exactly right.
      this.drillSub?.unsubscribe();
      this.drillSub = null;
      this.drillLoading = false;

      if (this.baseSeries) {
        this.rawSeries = this.baseSeries;
        this.resolution = this.baseResolution;
        this.dropStaleScenario();
      }
      this.baseSeries = null;
      this.baseResolution = null;

      this.spansMonths = this.countMonths(this.rawSeries) > 1;
      this.periodLabel = this.toPeriodLabel(this.rawSeries);
      this.render();
      this.touch();
      return;
    }

    const top = this.drillStack[this.drillStack.length - 1];
    this.fetchDrill(top.level, top.from, top.to, (series, resolution) => this.applyDrilled(series, resolution));
  }

  /** One step back up. */
  drillUp(): void {
    this.drillToDepth(Math.max(0, this.drillStack.length - 1));
  }

  /** What a crumb is called, in the unit the step landed on. */
  private crumbLabel(level: 'm' | 'w' | 'd' | 'h', from: Date, to: Date): string {
    if (level === 'm') {
      // Months of a quarter, or of a year.
      const months = (to.getFullYear() - from.getFullYear()) * 12 + to.getMonth() - from.getMonth();
      return months <= 3 ? bucketTitle(from, 'q') : String(from.getFullYear());
    }
    if (level === 'w') return formatDate(from, { month: 'long', year: 'numeric' });
    if (level === 'd') {
      const end = new Date(to.getTime() - 1);
      const sameMonth = end.getMonth() === from.getMonth();
      const a = formatDate(from, sameMonth ? { day: 'numeric' } : { day: 'numeric', month: 'short' });
      const b = formatDate(end, { day: 'numeric', month: 'short' });
      return `${a} to ${b}`;
    }
    return formatDate(from, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  }

  /**
   * Explicit size. ngx-charts only re-measures on a WINDOW resize, so with an auto width it kept
   * the width it had when the what-if panel was closed and the panel then overlapped the bars.
   */
  chartView: [number, number] = [600, 300];

  // ngx-charts' own legend renders inside the plot box, which put it ABOVE the month brackets.
  // Drawn in the template instead so it can sit at the bottom where it belongs.
  legend = false;
  xAxis = true;
  yAxis = true;
  showGridLines = true;
  roundDomains = true;

  constructor(
    private userMenuService: UserMenuService,
    private cdr: ChangeDetectorRef,
    private host: ElementRef<HTMLElement>,
    private zone: NgZone) { }

  ngOnChanges(changes: SimpleChanges): void {
    // Closing the what-if puts the metered figures back. The panel is destroyed on close and never
    // hands over an empty scenario on its way out, so the chart has to drop the last one itself.
    const whatIf = changes['showWhatIf'];
    if (whatIf && !whatIf.firstChange && !whatIf.currentValue) this.clearScenario();

    // Compare has no what-if. Say so the moment the box is ticked, not only once the comparison has
    // loaded: until then the header kept the button the bar chart had offered.
    if ('compare' in changes) this.publishAvailability();

    // Nothing to fetch until the dialog has the window. Whatever changed meanwhile is still on the
    // inputs, and is loaded the moment ready comes back.
    if (!this.ready) {
      this.cdr.markForCheck();
      return;
    }

    // Opening or closing the what-if, flipping H or T, is not new data. Reloading for it threw away the drill,
    // refetched the chart and the year of hours, and blanked the bars while it did.
    const names = Object.keys(changes);
    if (names.length && names.every(n => ResourceSeriesChartComponent.PRESENTATION_INPUTS.includes(n))) {
      this.scheduleOverlays();
      return;
    }

    // A different site, reading type, date range or period makes whatever day was drilled into
    // meaningless - it may not even be in the new window.
    this.resetDrill();
    this.load();
  }

  /** Inputs that only change how the same data is drawn, so never trigger a reload. */
  private static readonly PRESENTATION_INPUTS = ['showWhatIf', 'showLabels', 'showToolbar', 'chartTitle'];

  /** The ngx-charts instance of each style, whichever one is on screen. Its scales place the overlays. */
  @ViewChild(BarVerticalStackedComponent) private stackedChart?: BarVerticalStackedComponent;
  @ViewChild(BarVertical2DComponent) private groupedChart?: BarVertical2DComponent;
  @ViewChild(LineChartComponent) private lineChart?: LineChartComponent;

  private get chartInstance(): any {
    return this.chartStyle === 'stacked' ? this.stackedChart : this.chartStyle === 'grouped' ? this.groupedChart : this.lineChart;
  }

  ngAfterViewInit(): void {
    this.ensureObservers();
    this.scheduleOverlays();
  }

  /**
   * Attach the resize observer and the hover listeners, the first time there is a box to attach them to.
   *
   * `.rsc-chart` sits behind an *ngIf, so at ngAfterViewInit it may not exist yet. Doing this once at
   * ngAfterViewInit, which is what used to happen, silently attached nothing for the life of the
   * component.
   */
  private ensureObservers(): void {
    const chartBox = this.host.nativeElement.querySelector<HTMLElement>('.rsc-chart');
    if (!chartBox || chartBox === this.observedBox) return;

    this.resizeObserver?.disconnect();
    this.observedBox = chartBox;

    this.zone.runOutsideAngular(() => {
      // A browser resize does not run change detection on an OnPush component, so without this the
      // plot keeps the size it had.
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleOverlays());
        this.resizeObserver.observe(chartBox);
      }

      // The hover panel. Outside Angular: it is drawn straight into the box, and a mouse move must
      // not run change detection.
      chartBox.addEventListener('mousemove', e => this.onPlotHover(e));
      chartBox.addEventListener('mouseleave', () => this.hideTip());
    });
  }

  /**
   * Fires on every change-detection pass, which over this chart means every mouse move: ngx-charts
   * binds hover handlers per bar. So it only compares the overlays' signature, which is read off the
   * chart instance and costs nothing, and redraws only when that has moved.
   */
  ngAfterViewChecked(): void {
    this.ensureObservers();
    if (this.layoutSignature() !== this.lastLayoutSig) this.scheduleOverlays();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.overlayFrame !== null) cancelAnimationFrame(this.overlayFrame);
    if (this.settleFrame !== null) cancelAnimationFrame(this.settleFrame);
    this.cancelRequests();
  }

  private resizeObserver?: ResizeObserver;
  private observedBox: HTMLElement | null = null;
  private overlayFrame: number | null = null;
  private settleFrame: number | null = null;

  /** Bumped on every rebuild of chartData, so swapping one 31-day month for another still redraws. */
  private renderSeq = 0;

  /** The signature the overlays on screen were drawn for. */
  private lastLayoutSig = '';

  /**
   * Everything the overlays depend on, as one string: the data, the plot's size and place, the y
   * scale's domain, and what is switched on. Read off the ngx-charts instance rather than the page.
   */
  private layoutSignature(): string {
    const c = this.chartInstance;
    const y = c?.yScale ?? c?.valueScale;
    return [
      this.renderSeq, this.chartStyle, this.chartView.join('x'), this.showLabels, this.highlighted ?? '',
      c?.dims?.width, c?.dims?.height, c?.transform, typeof y?.domain === 'function' ? y.domain().join(',') : ''
    ].join('|');
  }

  /** Redraw on the next frame, once per burst, when ngx-charts has put the new bars on the page. */
  private scheduleOverlays(): void {
    if (this.overlayFrame !== null) return;
    this.zone.runOutsideAngular(() => {
      this.overlayFrame = requestAnimationFrame(() => {
        this.overlayFrame = null;
        this.drawOverlays();
        this.watchSettle(ResourceSeriesChartComponent.SETTLE_FRAMES);
      });
    });
  }

  private drawOverlays(): void {
    this.fitLegend();
    this.measureChart();
    this.renderOverlays();
  }

  /**
   * How many frames after a draw to keep comparing the chart's own numbers, about half a second.
   * ngx-charts measures its axis labels after its first pass and lays itself out again a tick later,
   * which moves the plot without anything of ours changing. Reading the instance is free, so this
   * never touches the page unless the plot has actually moved.
   */
  private static readonly SETTLE_FRAMES = 30;

  private watchSettle(frames: number): void {
    if (this.settleFrame !== null) cancelAnimationFrame(this.settleFrame);
    this.settleFrame = null;
    if (frames <= 0) return;

    this.settleFrame = requestAnimationFrame(() => {
      this.settleFrame = null;
      if (this.layoutSignature() !== this.lastLayoutSig) this.drawOverlays();
      this.watchSettle(frames - 1);
    });
  }

  /**
   * The gap between bars, as a share of each bar's slot rather than ngx-charts' fixed 8px.
   *
   * A fixed 8px is fine for a dozen bars and ruinous for a hundred: three months by day across
   * about 1,050px leaves each bar a 9px slot, so 8px of gap drew 1px lines (Jayden, 30/09/26,
   * against the SSRS chart, which keeps its bars wide at any range). A fifth of the slot keeps the
   * bars about four times the width of the gaps between them, and the gap never goes above the 8px
   * a short range always had.
   */
  get barGap(): number {
    const buckets = this.chartData.length;
    if (!buckets) return 8;
    const slot = this.chartView[0] / buckets;
    // Under 3px a slot has no room for a gap at all. d3 rounds the step down to whole pixels, and a
    // 1px gap in a 1px step left a bar of nothing: two years by day on a laptop drew an empty chart.
    if (slot < 3) return 0;
    return Math.max(1, Math.min(8, Math.round(slot * 0.2)));
  }

  /**
   * barGap, for the side-by-side chart, corrected for an ngx-charts bug.
   *
   * ngx-charts 20.1's bar-vertical-2d works out the gap between groups from the plot's HEIGHT,
   * spacing = groups / (dims.height / groupPadding + 1), although the groups are laid out across its
   * width (getGroupScale, swimlane-ngx-charts.mjs). The stacked chart uses the width, as it should.
   * So a 400px tall plot gave every group a gap of about 60% of its slot at four months by day and
   * 47% at a day by hour: Electricity Meters and Electrical Mains drew hairline bars with wide gaps
   * between them, against the SSRS chart's bars that nearly touch (Jayden, 01/10/26).
   *
   * Scaling the padding by height over width cancels the mistake, so the gap is barGap again. The
   * plot is the view less ngx-charts' axes and margins; an estimate is close enough, because it only
   * scales a gap of a few pixels.
   */
  get groupPadding(): number {
    const plotWidth = Math.max(1, this.chartView[0] - 90);
    const plotHeight = Math.max(1, this.chartView[1] - 70);
    return this.barGap * plotHeight / plotWidth;
  }

  /** Between the bars inside one group of a side-by-side chart: a hairline once they get narrow. */
  get groupedBarGap(): number {
    const buckets = this.chartData.length;
    const perGroup = (this.chartData[0] as any)?.series?.length || 1;
    if (!buckets || perGroup < 2) return 0;
    const bar = this.chartView[0] / buckets / perGroup;
    return Math.max(1, Math.min(4, Math.round(bar * 0.1)));
  }

  /**
   * Room under the plot for the legend as it actually is. It used to be a fixed one line, so a
   * legend that wrapped - eighteen meters at Killeshal - was drawn over the month brackets and the
   * dates. The stylesheet builds the plot's bottom padding and the brackets' position from this
   * (see .rsc-chart), and measureChart reads the padding back, so the plot gives way to it.
   */
  private fitLegend(): void {
    const chartBox = this.host.nativeElement.querySelector<HTMLElement>('.rsc-chart');
    if (!chartBox) return;
    const legend = chartBox.querySelector<HTMLElement>('.rsc-legend');
    // Before the first bars there is no legend yet: hold a line for it, so the plot is not drawn
    // a line too tall and then shrunk. The meter list replaces it outright.
    const h = legend ? legend.offsetHeight : (this.meterList ? 0 : 18);
    if (chartBox.style.getPropertyValue('--rsc-legend-h') !== h + 'px') {
      chartBox.style.setProperty('--rsc-legend-h', h + 'px');
    }
  }

  /** Re-measure only on a real change, or setting the view would loop against the observer. */
  private measureChart(): void {
    const chartBox = this.host.nativeElement.querySelector<HTMLElement>('.rsc-chart');
    if (!chartBox) return;

    // Everything in one view, however thin the bars get. A wide range is read for its shape, and
    // scrolling to see the rest of it loses exactly that.
    const full = Math.max(280, Math.floor(chartBox.clientWidth));

    // Compare puts its two pies in a column beside the bars, so the bars take the rest.
    const width = this.compare ? Math.max(240, Math.floor(full * 0.6)) : full;

    // Height follows the box the same way width does. It used to be pinned at 300 inside a box
    // pinned at 360, so on a short screen the dialog scrolled instead of the chart getting smaller -
    // and scrolling a chart to reach its own axis is the thing this view exists to avoid.
    //
    // clientHeight INCLUDES padding, and the box reserves padding-bottom for the month brackets and
    // the legend beneath them. Read that back off the element rather than repeating the number here,
    // so the reservation only ever lives in the stylesheet.
    const reserved = parseFloat(getComputedStyle(chartBox).paddingBottom) || 0;
    const height = Math.max(ResourceSeriesChartComponent.MIN_VIEW_H, Math.floor(chartBox.clientHeight - reserved));

    const column: [number, number] = [Math.max(160, full - width - 12), height];
    const columnMoved = Math.abs(column[0] - this.compareColumn[0]) >= 2 || Math.abs(column[1] - this.compareColumn[1]) >= 2;
    if (columnMoved) this.compareColumn = column;

    if (!columnMoved && Math.abs(width - this.chartView[0]) < 2 && Math.abs(height - this.chartView[1]) < 2) return;

    this.chartView = [width, height];
    this.zone.run(() => this.cdr.markForCheck());

    // The bars have not been laid out at the new width yet, so redraw the labels next frame.
    this.scheduleOverlays();
  }

  /**
   * In-flight requests, kept so a new one can cancel the last.
   *
   * Without this the chart pile-up was genuinely brutal. The hourly fetch below takes about EIGHT
   * SECONDS on the dev box and comes back with ~29,000 rows, so moving the date range five times in
   * quick succession left five of them running, five responses to parse, and five full chart
   * re-renders landing one after another - each on data for a window the user had already left.
   * Chrome offered "wait or exit page", which is exactly what it looks like from the outside.
   * Unsubscribing aborts the XHR, so only the window actually being looked at is ever applied.
   */
  private mainSub: Subscription | null = null;
  private hourlySub: Subscription | null = null;
  private drillSub: Subscription | null = null;
  private hourlyTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * How long the hourly fetch waits before it commits.
   *
   * Long enough that flicking through dates issues ONE eight-second query instead of one per stop,
   * short enough that nobody reading the chart notices. The main chart is never delayed by this: it
   * is on its own request and renders as soon as it arrives.
   */
  private static readonly HOURLY_DEBOUNCE_MS = 450;

  private load(): void {
    if (!this.siteId || !this.readingTypeId || !this.dateLeft || !this.dateRight) return;

    // Whatever was in flight is for a window nobody is looking at any more.
    this.cancelRequests();

    this.isLoading = true;
    this.errorMessage = null;
    this.cdr.markForCheck();

    const request$ = this.compare
      ? this.userMenuService.getResourceComparison$(
          this.siteId, this.readingTypeId, '1' + (this.period || 'd').slice(-1).toLowerCase(),
          this.compareLeft || this.dateLeft, this.compareRight || this.dateRight,
          this.reportUrl, this.groupType, this.groupId)
      : this.userMenuService.getResourceSeries$(
          this.siteId, this.readingTypeId, this.period, this.dateLeft, this.dateRight,
          this.reportUrl, this.groupType, this.groupId);

    this.mainSub = request$
      .subscribe({
        next: (response: ResourceSeriesResponse) => {
          const series = withColours(response?.series ?? []);
          this.unit = response?.unit ?? '';
          this.readingTypeName = response?.readingTypeName ?? '';
          this.averaged = response?.aggregation === SeriesAggregation.Average;
          this.resolution = response?.resolution ?? null;
          this.rawSeries = series;
          this.solarConfig = response?.solarConfig ?? null;
          this.tariffRates = response?.tariffRates ?? [];
          this.siteMic = response?.siteMic ?? null;
          this.measured = response?.measuredDemand ?? null;
          this.spansMonths = this.countMonths(series) > 1;
          this.scaleOptions = series.map(s => ({ id: s.logicalGroupId ?? null, name: s.name ?? '' }));
          // Colours and legend are set by renderStack, off the segments actually emitted - the
          // stored-solar band changes that list, so the response's series order is not enough.
          this.periodLabel = this.toPeriodLabel(series);
          // A fresh response means a fresh scenario. The panel recomputes off rawSeries and hands
          // one back; until it does, draw the metered numbers.
          this.scenarioSeries = null;
          this.scenarioBands = [];
          this.scenarioBasis = null;
          this.scenarioGuides = [];
          this.render();
          this.publishAvailability();
          this.isLoading = false;
          // New data means new geometry - let the labels redraw.
          this.touch();
          this.loadHourly();
        },
        error: () => {
          this.errorMessage = 'Could not load this chart.';
          this.isLoading = false;
          this.touch();
        }
      });
  }

  /**
   * Second fetch, always at hourly resolution, for the battery only.
   *
   * Separate from the chart's own load because the two want different things: the chart shows the
   * period the user picked, the battery needs the intraday shape that any coarser bucket has already
   * averaged away. Failing this request leaves the battery panel unavailable with a reason; it never
   * degrades to simulating on daily buckets, which would produce a confident wrong answer.
   */
  /** Drop anything in flight and any pending hourly fetch. */
  private cancelRequests(): void {
    this.mainSub?.unsubscribe();
    this.mainSub = null;
    this.hourlySub?.unsubscribe();
    this.hourlySub = null;
    this.drillSub?.unsubscribe();
    this.drillSub = null;
    this.drillLoading = false;
    if (this.hourlyTimer !== null) {
      clearTimeout(this.hourlyTimer);
      this.hourlyTimer = null;
    }
  }

  private loadHourly(): void {
    this.hourlyRows = [];
    this.hourlyLoaded = false;
    this.hourlyUnavailableReason = null;

    this.hourlySub?.unsubscribe();
    this.hourlySub = null;
    if (this.hourlyTimer !== null) clearTimeout(this.hourlyTimer);

    if (!this.siteId || !this.readingTypeId || !this.dateLeft || !this.dateRight) return;

    // Compare has no what-if, so nothing to fetch the hours for.
    if (this.compare) return;

    // Only a chart that covers the mains has anything for the battery to model, the same test the
    // header button is offered on.
    if (!this.canWhatIf) {
      this.hourlyUnavailableReason = 'This chart does not show the site\'s electricity mains, so there is nothing to model a battery on.';
      return;
    }

    this.hourlyTimer = setTimeout(
      () => this.fetchHourly(),
      ResourceSeriesChartComponent.HOURLY_DEBOUNCE_MS
    );
  }

  private fetchHourly(): void {
    this.hourlyTimer = null;

    this.hourlySub = this.userMenuService
      // The chart's own report and group, so the hours are the same meters as the bars. Without them
      // the request ran the remainder proc for the whole site, which only matched on Mains/Solar PV.
      .getResourceSeries$(this.siteId, this.readingTypeId, 'h', this.dateLeft, this.dateRight,
        this.reportUrl, this.groupType, this.groupId)
      .subscribe({
        next: (response: ResourceSeriesResponse) => {
          const series = response?.series ?? [];
          const grid = this.mains(series);

          if (!grid) {
            this.hourlyUnavailableReason =
              'This chart does not show the site\'s electricity mains, so there is nothing to model a battery on.';
            this.touch();
            return;
          }

          // Everything that is not generation adds up to the mains: on a remainder chart the total
          // row is what is left after the sub meters, so the mains is the whole stack. Generation is
          // every solar or other generating meter on the chart, and none at all on a site without.
          const generation = series.filter(s => s !== grid && isGenerationName(s.name));
          const supply = series.filter(s => !generation.includes(s));
          const sumBy = (list: ResourceSeriesVM[]) => {
            const m = new Map<string, number>();
            for (const s of list) for (const p of s.points ?? []) {
              const key = String(p.date);
              m.set(key, (m.get(key) ?? 0) + (p.value ?? 0));
            }
            return m;
          };
          const gridByTs = sumBy(supply);
          const solar = generation.length ? { points: [...sumBy(generation)].map(([date, value]) => ({ date, value })) } : null;

          // What one kWp would make each hour, from the EU's PVGIS model for the site's location. Only
          // sent where the chart has no generation, so the client can try an array the site does not
          // have yet.
          this.solarEstimateNote = response?.solarEstimateNote ?? null;
          this.solarYearByDay = response?.solarYearByDay?.length === 366 ? response.solarYearByDay : null;
          const perKwp = new Map<string, number>();
          for (const p of response?.solarPerKwp ?? []) perKwp.set(String(p.date), p.value ?? 0);

          // Join on the interval start. A missing reading is skipped rather than zero-filled: a
          // zero would read as "nothing happened", which is a very different claim from "we did not
          // measure".
          //
          // ONE EXCEPTION, and it is not a fudge. Hours BEFORE the generation series begins are
          // zero-filled, because there the zero is the truth: the array did not exist yet, so it
          // generated nothing. Lee Strand's mains data starts Oct 2024 and its array was
          // commissioned in Apr 2025, and skipping those five months meant the hourly set covered a
          // shorter window than the chart. The totals then refused to correct themselves (they
          // compare their own span against the chart's and back off when the two disagree), so the
          // tiles fell back to bucketed figures, which net import against export and reported
          // "Exported to grid 0 kWh" on a site that exported 10.8 MWh.
          //
          // Gaps INSIDE the generation series are still skipped. A hole in the middle of a
          // commissioned array's record is missing data, not a quiet day, and zero-filling it would
          // understate generation and overstate the load the grid appears to be carrying.
          const solarByTs = new Map<string, number>();
          for (const p of solar?.points ?? []) solarByTs.set(String(p.date), p.value ?? 0);

          let solarStart = Number.POSITIVE_INFINITY;
          for (const p of solar?.points ?? []) {
            if (!p.date) continue;
            const t = new Date(p.date as any).getTime();
            if (!isNaN(t) && t < solarStart) solarStart = t;
          }

          const rows: BatteryHour[] = [];
          for (const [key, gridKwh] of gridByTs) {
            const ts = new Date(key as any);
            if (isNaN(ts.getTime())) continue;

            let solarKwh: number;
            if (solarByTs.has(key)) {
              solarKwh = solarByTs.get(key)!;
            } else if (ts.getTime() < solarStart) {
              solarKwh = 0;                       // before the array existed, or no array at all
            } else {
              continue;                           // a real gap, leave it out
            }

            const row: BatteryHour = { ts, solarKwh, gridKwh };
            if (perKwp.has(key)) row.kwpKwh = perKwp.get(key)!;
            rows.push(row);
          }
          rows.sort((a, b) => a.ts.getTime() - b.ts.getTime());

          // Two consecutive readings an hour apart is the whole premise. Anything coarser and the
          // simulation is measuring the wrong thing.
          const stepHours = rows.length > 1
            ? Math.abs(rows[1].ts.getTime() - rows[0].ts.getTime()) / 3600000
            : 0;

          if (rows.length < 2 || stepHours > 1.01) {
            this.hourlyUnavailableReason =
              'No hourly readings for this site over these dates, so a battery cannot be modelled.';
            this.touch();
            return;
          }

          this.hourlyRows = rows;
          this.hourlyLoaded = true;
          // The tiles are recomputed too: the import/export split only exists at this resolution.
          this.render();
          this.touch();
        },
        error: () => {
          this.hourlyUnavailableReason = 'Could not load hourly readings for the battery.';
          this.touch();
        }
      });
  }


  private withExportBand(bars: StackedPoint[], gridName: string): StackedPoint[] {
    return bars.map(bar => {
      const idx = bar.series.findIndex(s => s.name === gridName);
      if (idx < 0 || bar.series[idx].value >= 0) return bar;

      const series = bar.series.slice();
      series[idx] = { ...series[idx], name: ResourceSeriesChartComponent.EXPORT_BAND };
      return { ...bar, series };
    });
  }


  /**
   * Colour domain and legend, built from the segment names actually emitted rather than from the
   * response's series order.
   *
   * ngx-charts maps an ordinal domain by index into the label list it derives from the results, so
   * the domain has to follow the emitted order exactly. Deriving it here means inserting a band
   * cannot silently shift a series onto the wrong colour.
   */
  private applyColours(bars: StackedPoint[], series: ResourceSeriesVM[], bands: SeriesBand[] = []): void {
    const rgbByName = new Map<string, string>();

    // Each series' own colour, then each band's own colour, then the chart's one derived band. No
    // battery name appears here any more: a band carries its colour with it, so this keeps working
    // for whatever gets injected later without this method learning what any of it means.
    for (const s of series) rgbByName.set(s.name ?? '', s.rgb || '#999999');
    for (const b of bands) rgbByName.set(b.name, b.rgb);
    rgbByName.set(ResourceSeriesChartComponent.EXPORT_BAND, this.exportBandRgb(series));

    const order: string[] = [];
    for (const bar of bars) {
      for (const seg of bar.series) {
        if (!order.includes(seg.name)) order.push(seg.name);
      }
    }

    // No bars yet: fall back to the response order so the legend is not empty while loading.
    const names = order.length ? order : series.map(s => s.name ?? '');

    this.colours = { ...this.colours, domain: names.map(n => rgbByName.get(n) ?? '#999999') };
    this.legendItems = names.map(n => ({ name: n, rgb: rgbByName.get(n) ?? '#999999' }));
  }


  /**
   * Colour for units sold to the grid: the generation colour lightened.
   *
   * Same reasoning as the battery band, in the other direction. Below the axis everything came from
   * solar, so both bands stay in the generation colour's family - deepened for what was kept,
   * lightened for what was sold - and the grid's own colour is then free to mean "bought" and
   * nothing else. Deliberately NOT a new hue: a third unrelated colour on a stacked bar is what
   * made the old chart hard to read.
   */
  /**
   * Grey. It used to be the generation colour paled, the same pale as the what-if's spare solar going
   * into the battery, and on a sunny day the two could not be told apart (Tesco pictures, 09/10/26).
   * Energy that left the site is the one thing on the chart the site does not use, so it is neutral.
   */
  private exportBandRgb(_series: ResourceSeriesVM[]): string {
    return '#BDBDBD';
  }


  // ---- The overlays --------------------------------------------------------------------------------
  //
  // Numbers on the bars, column totals, month brackets, the what-if's guides and the hover panel. All
  // of them are laid out by overlay-layout.ts on the geometry plot-geometry.ts reads off the chart's
  // own scales. They used to be placed by reading every bar's rectangle back off the page, which broke
  // on every empty bucket, because ngx-charts hides a zero bar and a hidden element has no box.

  /** The geometry the overlays on screen were laid out on. Null until the chart has laid itself out. */
  private geo: PlotGeometry | null = null;

  /** Where the chart's svg sits in .rsc-chart, which the overlays are positioned in. */
  private svgOffset = { x: 0, y: 0 };

  /** The SSRS report this chart stands in for, by the way it writes its numbers. */
  get reportKind(): ReportKind {
    return reportKind(this.reportUrl, this.compare);
  }

  /** A figure as this chart's report writes it, for the template. */
  fmt(value: number | null | undefined): string {
    return formatValue(value, this.reportKind, this.averaged);
  }

  /** Brackets under the axis: months under days and weeks, the day under hours. */
  get useBands(): boolean {
    if (this.compare) return false;
    if (this.periodKey === 'h') return this.bucketDates.length > 0;
    return this.useMonthBands;
  }

  private renderOverlays(): void {
    const chartBox = this.host.nativeElement.querySelector<HTMLElement>('.rsc-chart');
    if (!chartBox) return;

    // Cleared first, before anything can bail out: the overlays hold no link to the bars they belong
    // to, so the moment the data changes every one of them is stale.
    chartBox.querySelectorAll('.rsc-seg-label, .rsc-seg-leader, .rsc-month-band, .rsc-guide').forEach(n => n.remove());
    // The hover panel describes the bars that were there.
    this.hideTip();
    this.geo = null;

    if (!this.chartData.length) {
      this.lastLayoutSig = this.layoutSignature();
      return;
    }

    const chart = this.chartInstance;
    const dates = this.bucketDates.map(d => d && !isNaN(d.getTime()) ? d : null);
    const buckets: GeoBucket[] = this.chartData.map((b, i) => ({ key: b.name, date: dates[i] ?? null, segments: b.series }));
    const geo = plotGeometry(chart, this.chartStyle, buckets);
    const svg = (chart?.chartElement?.nativeElement as HTMLElement | undefined)?.querySelector('svg');
    // Not laid out yet. The next change-detection pass or settle frame comes back for it.
    if (!geo || !svg) return;

    this.geo = geo;
    this.lastLayoutSig = this.layoutSignature();

    // The one measurement left: where the svg itself sits in the box.
    const box = chartBox.getBoundingClientRect();
    const at = svg.getBoundingClientRect();
    const dx = at.left - box.left, dy = at.top - box.top;
    this.svgOffset = { x: dx, y: dy };

    const out = document.createDocumentFragment();

    if (this.useBands) {
      const measure = this.bandMeasure(chartBox);
      for (const b of layoutBands(dates, geo, this.periodKey === 'h' ? 'day' : 'month', measure)) out.appendChild(this.bandEl(b, dx));
    }

    for (const g of layoutGuides(this.liveScenarioGuides, dates, geo, this.periodKey === 'h')) out.appendChild(this.guideEl(g, dx, dy));

    if (this.showLabels && this.chartStyle !== 'line') {
      const font = this.labelFont(chartBox);
      const { labels, leaders } = layoutLabels(buckets, geo, {
        kind: this.reportKind,
        averaged: this.averaged,
        // A stack of several meters labels only the segments a number fits inside, and the column gets
        // its total on top. Two series keep the old rule, which Jayden signed off on Mains/Solar PV.
        busyStack: this.chartStyle === 'stacked' && this.meterCount >= ResourceSeriesChartComponent.BUSY_STACK,
        fills: new Map(this.legendItems.map(l => [l.name, l.rgb])),
        measure: font.measure,
        fontSize: font.size,
        lineHeight: font.lineHeight,
        boxHeight: box.height - dy
      });
      for (const l of leaders) out.appendChild(this.leaderEl(l, dx, dy));
      for (const l of labels) out.appendChild(this.labelEl(l, dx, dy));
    }

    chartBox.appendChild(out);
  }

  private labelEl(l: LabelOut, dx: number, dy: number): HTMLElement {
    const el = document.createElement('span');
    el.className = 'rsc-seg-label';
    el.textContent = l.text;
    if (l.outside) el.classList.add('rsc-seg-label-out');
    if (l.total) el.classList.add('rsc-col-total');
    else if (l.series) el.dataset['series'] = l.series;
    // A segment's number that will not fit inside it, on a tag in the segment's colour, so it reads as
    // that segment's and not as a figure of its own (Lodge Road, 07/10/26).
    if (l.tag && l.fill) {
      el.classList.add('rsc-seg-label-tag');
      el.style.background = l.fill;
    }
    if (l.dark) el.classList.add('rsc-seg-label-dark');
    if (l.turned) {
      el.classList.add('rsc-seg-label-turned');
      // Read by the image export, which draws text level unless told otherwise.
      el.dataset['rotate'] = '-90';
    }
    if (l.fontSize) el.style.fontSize = l.fontSize + 'px';
    // How far it was pulled from its segment's middle, for the page tests to read.
    if (l.pulled !== undefined) el.dataset['pulled'] = String(l.pulled);
    if (this.highlighted && l.series && l.series !== this.highlighted) el.style.opacity = '0';
    el.setAttribute('aria-hidden', 'true');
    el.style.left = (l.x + dx) + 'px';
    el.style.top = (l.y + dy) + 'px';
    return el;
  }

  private leaderEl(l: LeaderOut, dx: number, dy: number): HTMLElement {
    const el = document.createElement('span');
    el.className = 'rsc-seg-leader';
    el.style.left = (l.x + dx) + 'px';
    el.style.top = (l.top + dy) + 'px';
    el.style.height = l.height + 'px';
    return el;
  }

  private bandEl(b: BandOut, dx: number): HTMLElement {
    const el = document.createElement('div');
    el.className = 'rsc-month-band';
    el.setAttribute('aria-hidden', 'true');
    el.style.left = (b.left + dx) + 'px';
    el.style.width = Math.max(0, b.width) + 'px';
    const label = document.createElement('span');
    label.className = 'rsc-month-band-label';
    label.textContent = b.label;
    el.appendChild(label);
    return el;
  }

  private guideEl(g: GuideOut, dx: number, dy: number): HTMLElement {
    const el = document.createElement('div');
    el.className = 'rsc-guide ' + (g.kind === 'level' ? 'rsc-guide-level' : 'rsc-guide-hours' + (g.warm ? ' rsc-guide-warm' : ''));
    el.setAttribute('aria-hidden', 'true');
    el.style.left = (g.box.left + dx) + 'px';
    el.style.width = Math.max(0, g.box.width) + 'px';
    el.style.top = (g.box.top + dy) + 'px';
    if (g.kind === 'hours') el.style.height = Math.max(0, g.box.height) + 'px';
    const label = document.createElement('span');
    label.className = 'rsc-guide-label';
    label.textContent = g.label;
    el.appendChild(label);
    return el;
  }

  /**
   * The label font, measured once off one label on the page: its size, its line height, and widths
   * from a canvas. Putting every label on the page to measure it forced a layout per bar.
   */
  private labelFont(chartBox: HTMLElement): { size: number; lineHeight: number; measure: (t: string) => number } {
    if (this.font) return this.font;
    const probe = document.createElement('span');
    probe.className = 'rsc-seg-label';
    probe.textContent = '0';
    chartBox.appendChild(probe);
    const cs = getComputedStyle(probe);
    const size = parseFloat(cs.fontSize) || 11;
    const lineHeight = probe.offsetHeight;
    const ctx = document.createElement('canvas').getContext('2d')!;
    ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    probe.remove();
    const font = { size, lineHeight: lineHeight || size, measure: (t: string) => ctx.measureText(t).width };
    // Not kept from a box that is not on screen, which measures nothing.
    if (lineHeight) this.font = font;
    return font;
  }
  private font?: { size: number; lineHeight: number; measure: (t: string) => number };

  /** Measures a month bracket's label in its own font (.rsc-month-band-label), or undefined off screen. */
  private bandMeasure(chartBox: HTMLElement): ((t: string) => number) | undefined {
    if (this.bandFont) return this.bandFont;
    const probe = document.createElement('span');
    probe.className = 'rsc-month-band-label';
    probe.textContent = '0';
    chartBox.appendChild(probe);
    const cs = getComputedStyle(probe);
    const shown = probe.offsetHeight > 0;
    const ctx = document.createElement('canvas').getContext('2d')!;
    ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    probe.remove();
    if (!shown) return undefined;
    return this.bandFont = (t: string) => ctx.measureText(t).width;
  }
  private bandFont?: (t: string) => number;

  // ---- The hover panel ------------------------------------------------------------------------

  private tipEl?: HTMLElement;

  /**
   * Every meter's figure for the column under the pointer, biggest first, with the column's total.
   * Replaces ngx-charts' tooltip on a busy chart, which names one segment at a time: on a column of
   * 18 thin strips that is no way to read it. The segment actually under the pointer is in bold.
   *
   * The column is found from the pointer's place on the axis, as ngx-charts' own tooltip area finds
   * it, so an empty day is its own column rather than a gap given to its neighbours.
   */
  private onPlotHover(e: MouseEvent): void {
    const chartBox = this.host.nativeElement.querySelector<HTMLElement>('.rsc-chart');
    const geo = this.geo;
    if (!chartBox || !geo || !this.columnTips) { this.hideTip(); return; }

    const box = chartBox.getBoundingClientRect();
    const x = e.clientX - box.left, y = e.clientY - box.top;
    const px = x - this.svgOffset.x, py = y - this.svgOffset.y;
    if (py < geo.plot.top - 4 || py > geo.plot.top + geo.plot.height + 4) { this.hideTip(); return; }

    const i = bucketAt(geo, px);
    const bucket = i >= 0 ? this.chartData[i] : undefined;
    if (!bucket) { this.hideTip(); return; }

    const under = (e.target as Element | null)?.closest?.('path.bar')?.getAttribute('fill')?.toLowerCase();
    const rgbOf = new Map(this.legendItems.map(l => [l.name, l.rgb]));
    const rows = bucket.series
      .filter(s => s.value !== 0)
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
    const total = bucket.series.reduce((a, s) => a + s.value, 0);
    const fmt = (v: number) => formatWithUnit(v, this.unit, this.reportKind, this.averaged);

    const tip = this.tipEl ??= (() => {
      const el = document.createElement('div');
      el.className = 'rsc-col-tip';
      el.dataset['export'] = 'skip';
      return el;
    })();
    if (tip.parentElement !== chartBox) chartBox.appendChild(tip);

    tip.textContent = '';
    const head = document.createElement('div');
    head.className = 'rsc-col-tip-head';
    const date = this.bucketDates[i];
    head.textContent = date ? bucketTitle(date, this.periodKey) : this.toTick(bucket.name);
    tip.appendChild(head);
    if (!bucket.series.length) {
      const none = document.createElement('div');
      none.className = 'rsc-col-tip-row rsc-col-tip-none';
      none.textContent = 'No readings';
      tip.appendChild(none);
    }
    for (const s of rows) {
      const rgb = rgbOf.get(s.name) ?? '#999999';
      const row = document.createElement('div');
      row.className = 'rsc-col-tip-row' + (under && rgb.toLowerCase() === under ? ' rsc-col-tip-here' : '');
      const sw = document.createElement('i');
      sw.style.background = rgb;
      const name = document.createElement('span');
      name.textContent = s.name;
      const val = document.createElement('b');
      val.textContent = fmt(s.value);
      row.append(sw, name, val);
      tip.appendChild(row);
    }
    if (!this.averaged && bucket.series.length) {
      const foot = document.createElement('div');
      foot.className = 'rsc-col-tip-row rsc-col-tip-total';
      const name = document.createElement('span');
      name.textContent = 'Total';
      const val = document.createElement('b');
      val.textContent = fmt(total);
      foot.append(document.createElement('i'), name, val);
      tip.appendChild(foot);
    }

    // Beside the pointer, flipped to the other side near the right edge, and kept inside the box.
    tip.style.display = 'block';
    const w = tip.offsetWidth, h = tip.offsetHeight;
    const left = x + 16 + w > box.width ? x - 16 - w : x + 16;
    tip.style.left = Math.max(0, left) + 'px';
    tip.style.top = Math.max(0, Math.min(box.height - h, y - h / 2)) + 'px';
  }

  /**
   * ngx-charts' own tooltip, for one segment or one point, in the chart's own words: the bucket's full
   * date and the figure in the report's format. Its default printed the raw key and value
   * ("2026-08-24 • PCL Head Office Mains", "525.405"), the one place on the chart that did (14/10/26).
   *
   * A bar's model is { series: bucket key, name: meter }; a line point's is { series: meter, name: Date }.
   */
  tip(model: any): { title: string; name: string; value: string } {
    const point = model?.name instanceof Date;
    const at = point ? model.name : model?.series;
    return {
      title: this.bucketName(at),
      name: String((point ? model.series : model?.name) ?? ''),
      value: formatWithUnit(Number(model?.value ?? 0), this.unit, this.reportKind, this.averaged)
    };
  }

  /** A bucket's full name, from its key or its date: "Week of 22 Jun 2026", or a Compare period. */
  private bucketName(at: any): string {
    if (this.compare) return this.compareLabel(at) ?? this.toTick(at);
    if (at instanceof Date) return isNaN(at.getTime()) ? '' : bucketTitle(at, this.periodKey);
    const i = this.chartData.findIndex(b => b.name === at);
    const date = i >= 0 ? this.bucketDates[i] : undefined;
    return date && !isNaN(date.getTime()) ? bucketTitle(date, this.periodKey) : this.toTick(at);
  }

  private hideTip(): void {
    if (this.tipEl) this.tipEl.style.display = 'none';
  }


  // ---- The scenario the what-if is asking for ----------------------------------------------------
  //
  // Everything the chart knows about the what-if is here, and it is four fields and one handler.
  // The chart cannot tell a battery band from any other band, and does not know what a multiplier
  // means: it is handed series to draw, bands to overlay, and the two numbers the tiles need to
  // stay consistent with the bars.

  /** Series to draw in place of the response's own. Null means draw what was loaded. */
  private scenarioSeries: ResourceSeriesVM[] | null = null;

  /** Bands to overlay, applied before the chart's own export split. See series-bands.ts, rule 1. */
  private scenarioBands: SeriesBand[] = [];

  /**
   * The scenario's generation multiplier and interval cap.
   *
   * Only the tiles need these, and only because they recompute the gross import/export split from
   * hourly rows and would otherwise disagree with the bars. Once the endpoint returns that split
   * ready-made, both of these and the hourly fetch leave this component together.
   */
  private scenarioMultiplier = 1;

  /** kWp of estimated solar the scenario adds on a site with none metered. See WhatIfScenario. */
  private scenarioAddedKwp = 0;
  private scenarioCap: number | null = null;

  /** Wording for the tiles, handed over rather than worked out: the chart does not know what changed. */
  scenarioNote: string | null = null;

  /** The rawSeries the current scenario was worked out from. See WhatIfScenario.basis. */
  private scenarioBasis: ResourceSeriesVM[] | null = null;

  /**
   * Whether the scenario was worked out from the series on screen. Every read of the scenario goes
   * through the getters below, so a stale one, whichever order it arrived in, is simply not there:
   * the chart draws its readings with no bands until the panel hands over a scenario for them.
   */
  private get scenarioFresh(): boolean {
    return this.scenarioBasis !== null && this.scenarioBasis === this.rawSeries;
  }
  private get liveScenarioSeries(): ResourceSeriesVM[] | null { return this.scenarioFresh ? this.scenarioSeries : null; }
  private get liveScenarioBands(): SeriesBand[] { return this.scenarioFresh ? this.scenarioBands : []; }
  private get liveMultiplier(): number { return this.scenarioFresh ? this.scenarioMultiplier : 1; }
  private get liveCap(): number | null { return this.scenarioFresh ? this.scenarioCap : null; }
  private get liveScenarioGuides(): ChartGuide[] { return this.scenarioFresh ? this.scenarioGuides : []; }

  /** The tiles' note, only while its scenario is the one drawn. */
  get liveScenarioNote(): string | null { return this.scenarioFresh ? this.scenarioNote : null; }

  /** Lines and shaded hours from the what-if, drawn over the bars by drawGuides(). */
  private scenarioGuides: ChartGuide[] = [];

  /** Why hourly readings could not be loaded, passed to the panel which is where it is shown. */
  hourlyUnavailableReason: string | null = null;

  /** Exclusive end of the displayed window, so the panel can bound its last bucket. */
  get bucketWindowEnd(): Date | null {
    const top = this.drillStack.length ? this.drillStack[this.drillStack.length - 1] : null;
    if (top) return top.to;
    return this.hourlyRows.length ? new Date(this.hourlyRows[this.hourlyRows.length - 1].ts.getTime() + 3600000) : null;
  }

  /**
   * Back to the metered figures.
   *
   * Until 24/09/26 nothing did this on purpose: closing the panel wrongly reloaded the whole chart,
   * and the reload happened to reset the scenario. Once closing stopped reloading, the chart kept
   * drawing the last scenario with no panel on screen to explain it.
   */
  private clearScenario(): void {
    if (this.scenarioSeries === null && !this.scenarioBands.length && this.scenarioNote === null
        && this.scenarioMultiplier === 1 && this.scenarioCap === null && !this.scenarioGuides.length) return;
    this.scenarioSeries = null;
    this.scenarioBands = [];
    this.scenarioGuides = [];
    this.scenarioMultiplier = 1;
    this.scenarioAddedKwp = 0;
    this.scenarioCap = null;
    this.scenarioNote = null;
    this.scenarioBasis = null;
    this.render();
    this.touch();
  }

  /** The what-if has recomputed. Redraw with whatever it decided. */
  onScenario(s: WhatIfScenario): void {
    this.scenarioSeries = s.series;
    this.scenarioGuides = s.guides ?? [];
    this.scenarioBands = s.bands;
    this.scenarioMultiplier = s.multiplier;
    this.scenarioAddedKwp = s.addedKwp ?? 0;
    this.scenarioCap = s.cap;
    this.scenarioNote = s.note;
    this.scenarioBasis = s.basis ?? null;
    this.render();
    this.touch();
  }


  /**
   * Draw. Replaces applyMultiplier, which used to both work out the scenario and render it; the
   * working out went with the what-if and only the rendering stayed.
   */
  private render(): void {
    const series = this.liveScenarioSeries ?? this.rawSeries;

    // Metered figures straight off the response: the server works the gross split out at the grain
    // the meter recorded, for exactly the window on screen, drilled or not.
    this.baseTotals = this.toTotals(this.rawSeries, true);
    this.renderStack(series);

    // A scenario changes the hours themselves, which only the hourly rows can re-split. Until the
    // what-if hands its own split back with the scenario, that stays here.
    const scenario = this.liveScenarioSeries !== null || this.liveMultiplier !== 1 || this.liveCap !== null;
    this.totals = scenario
      ? this.withHourlySplit(this.toTotals(series, false), this.liveMultiplier)
      : this.baseTotals;
    this.lastLabelRedraw();
  }

  /**
   * The grid series, or undefined where this chart is not a grid and generation pair.
   *
   * pickGrid decides from the names (see there), and only a stacked chart of logical groups can
   * have one: a policy is a calculation, and an averaged reading (a temperature) has no import or
   * export.
   */
  private grid(series: ResourceSeriesVM[]): ResourceSeriesVM | undefined {
    if (this.chartStyle !== 'stacked' || this.averaged) return undefined;
    if (!series.length || series.some(s => s.logicalGroupId == null)) return undefined;
    return pickGrid(series, this.gridLogicalGroupId);
  }

  /**
   * The mains wherever the chart shows it, for the what-if. See pickMains. The same guards as grid()
   * except the stacking: a chart of the mains on its own is one series, and one series side by side
   * with nothing is the same picture stacked.
   */
  private mains(series: ResourceSeriesVM[]): ResourceSeriesVM | undefined {
    if (this.averaged) return undefined;
    if (!series.length || series.some(s => s.logicalGroupId == null)) return undefined;
    if (this.chartStyle !== 'stacked' && series.length !== 1) return undefined;
    return pickMains(series, this.gridLogicalGroupId);
  }

  /**
   * How this report is drawn, following the SSRS report it replaces: policy reports stack, the
   * consumption report puts its groups side by side, and the status report is a smooth line. The
   * remainder report is side by side in SSRS but stacks here, by decision (23/09/26): its segments
   * add up to the site total, and that is the view already demoed.
   */
  get chartStyle(): 'stacked' | 'grouped' | 'line' {
    // Two periods side by side, as both SSRS Compare reports draw them.
    if (this.compare) return 'grouped';
    const r = (this.reportUrl || '').toLowerCase();
    if (r === 'rptresourcestatus') return 'line';
    // A consumption chart of ONE meter is stacked while the what-if is open: one series looks the
    // same either way, and stacked is the only style the estimated solar and the battery's bands can
    // sit on the mains in. Without it an imported store's chart showed the battery in the figures and
    // nowhere on the bars (09/10/26, the Tesco stores for Donal).
    if (r === 'rptresourceconsumption') return this.showWhatIf && this.rawSeries.length === 1 ? 'stacked' : 'grouped';
    return 'stacked';
  }

  /**
   * The endpoint averaged these readings rather than summing them (temperatures, voltages and the
   * like). Adding them up means nothing, so the tiles show each series' average and no total.
   */
  averaged = false;

  /**
   * The same buckets as chartData, one line per series, for the line chart, on a time axis.
   *
   * By date, not by the bucket's text. On text ngx-charts lays the axis out in the order the series
   * first mention each bucket, so a series that starts later or has a gap put its dates after
   * everyone else's. A time axis is in date order whatever the series hold.
   *
   * Built once per render: a getter that returned a new array made ngx-charts redraw the whole line
   * on every change-detection pass, which is every mouse move.
   */
  get lineData(): { name: string; series: { name: Date; value: number }[] }[] {
    if (this.lineDataSeq === this.renderSeq) return this.lineDataCache;
    const bySeries = new Map<string, { name: Date; value: number }[]>();
    this.chartData.forEach((bucket, i) => {
      const date = this.bucketDates[i];
      if (!date) return;
      for (const seg of bucket.series) {
        if (!bySeries.has(seg.name)) bySeries.set(seg.name, []);
        bySeries.get(seg.name)!.push({ name: date, value: seg.value });
      }
    });
    this.lineDataCache = Array.from(bySeries, ([name, series]) => ({ name, series }));
    this.lineDataSeq = this.renderSeq;
    return this.lineDataCache;
  }
  private lineDataCache: { name: string; series: { name: Date; value: number }[] }[] = [];
  private lineDataSeq = -1;

  /**
   * The ticks the x axis writes: every bucket when they fit, otherwise every k-th, so the axis always
   * says something. The day numbers used to be blanked altogether below 18px a bar, which on a line
   * chart, with no month brackets drawn under it, left an axis with nothing on it at all.
   */
  get axisTicks(): (string | Date)[] | undefined {
    const n = this.chartData.length;
    const key = `${this.renderSeq}|${this.chartView[0]}|${this.chartStyle}`;
    if (key === this.axisTicksKey) return this.axisTicksCache;
    this.axisTicksKey = key;
    const slot = n ? this.chartView[0] / n : Infinity;
    const minPx = this.periodKey === 'd' && !this.useMonthBands ? 44 : this.periodKey === 'm' || this.periodKey === 'w' ? 52 : 22;
    const every = Math.max(1, Math.ceil(minPx / slot));
    const names: (string | Date)[] = this.chartStyle === 'line' ? this.bucketDates : this.chartData.map(b => b.name);
    this.axisTicksCache = every === 1 && this.chartStyle !== 'line' ? undefined : names.filter((_, i) => i % every === 0);
    return this.axisTicksCache;
  }
  private axisTicksKey = '';
  private axisTicksCache: (string | Date)[] | undefined;

  readonly curve = curveMonotoneX;

  private sum(s: ResourceSeriesVM, keep: (v: number) => boolean): number {
    return sumSeries(s, keep);
  }


  /**
   * Bucket the series into bars, add the stored-solar band where it applies, and set the colours to
   * match whatever segments came out.
   *
   * Colours are set from the emitted bars rather than at load time because the band changes the
   * segment list, and an ordinal domain that does not follow the emitted order hands the series each
   * other's colours.
   */
  private renderStack(series: ResourceSeriesVM[]): void {
    let bars = this.toStacked(series);   // also fills bucketDates, which the fold below needs
    const gridName = this.grid(series)?.name ?? null;

    // Bands the what-if wants drawn. Described, not drawn, by whatever asked for them: see
    // series-bands.ts, which is the contract this component shares with the what-if and is the only
    // thing either side needs to agree on.
    let bands: SeriesBand[] = [];

    if (gridName && this.liveScenarioBands.length) {
      bands = this.liveScenarioBands;
      bars = applyBands(bars, bands);
    }

    // Always, not only under the battery panel: the same red meaning both bought and sold is
    // confusing whether or not a battery is being modelled. Runs AFTER the injected bands, and that
    // order is a value dependency rather than a preference - see rule 1 in series-bands.ts.
    if (gridName) bars = this.withExportBand(bars, gridName);

    this.applyColours(bars, series, bands);
    this.chartData = bars;
    this.renderSeq++;
  }


  private lastLabelRedraw(): void {
    this.scheduleOverlays();
    this.cdr.markForCheck();
  }

  /**
   * Rows arrive one per group per interval, interleaved, and the group order changes between
   * dates - so bucket by the interval's own label rather than assuming any ordering.
   */
  private toStacked(series: ResourceSeriesVM[]): StackedPoint[] {
    const buckets = new Map<string, StackedPoint>();
    const order: string[] = [];
    const dates = new Map<string, Date | null>();

    for (const s of series) {
      for (const p of s.points ?? []) {
        // Key on the date. Keying on the visible tick merged every 12th of the month into one bar
        // once the ticks were shortened to the day number. Read off the server's own text (see
        // bucketKey): through a Date, 01:00 on the morning the clocks go forward is 02:00, and the
        // 02:00 bar was drawn twice its height.
        const dated = bucketKey(p.date as any, this.periodKey);
        const key = dated ?? (p.label ?? String(p.date));
        if (!buckets.has(key)) {
          buckets.set(key, { name: key, series: [] });
          order.push(key);
          dates.set(key, dated ? keyDate(dated) : null);
        }
        buckets.get(key)!.series.push({ name: s.name ?? '', value: p.value ?? 0 });
      }
    }

    // Chronological, NOT first-appearance order.
    //
    // Buckets are discovered by walking the series in turn, so the order they appear in depends on
    // which series happens to mention a date first. That is fine while every series covers the same
    // span, and wrong the moment one starts later: Solar PV sorts before The Mains and only begins
    // when the array was commissioned, so the months only the mains covers were appended AFTER
    // everything else. The axis read Apr 2025 ... Sep 2026, Nov 2024 ... Mar 2025.
    //
    // A dated key sorts as text ("2026-08-17 13:00"). Buckets with no date keep their relative order
    // and sit at the end, since there is nothing to sort them by.
    // A period with no readings at all is not returned, so it is put back as an empty slot: see
    // fillGaps. Without it the bars either side closed up and the axis skipped the week unannounced.
    // Not in Compare: its two columns are the two periods chosen, not a stretch of time, and May
    // against September grew three empty months between them (14/10/26).
    const ordered = order.slice().sort((a, b) => {
      const da = dates.get(a), db = dates.get(b);
      if (da && db) return a < b ? -1 : a > b ? 1 : 0;
      if (da || db) return da ? -1 : 1;
      return order.indexOf(a) - order.indexOf(b);
    });
    const sorted = this.compare ? ordered : fillGaps(ordered, this.periodKey);
    for (const k of sorted) {
      if (buckets.has(k)) continue;
      buckets.set(k, { name: k, series: [] });
      dates.set(k, keyDate(k));
    }

    // Reuse the existing array when the buckets have not actually moved.
    //
    // This is an identity guard, not a micro-optimisation. bucketDates is an @Input to the what-if,
    // and Angular compares inputs by reference: handing out a fresh array on every render made the
    // panel recompute, emit a scenario, and send the chart round again, which allocated another
    // fresh array. A render loop that never settles, paid for on every change-detection pass, and
    // it presents as the whole page feeling sluggish rather than as anything obviously broken.
    const next = sorted.map(k => dates.get(k) ?? new Date(NaN));
    const same = next.length === this.bucketDates.length
      && next.every((d, i) => d.getTime() === this.bucketDates[i]?.getTime() || (isNaN(d.getTime()) && isNaN(this.bucketDates[i]?.getTime())));
    if (!same) this.bucketDates = next;

    return sorted.map(k => buckets.get(k)!);
  }

  /** One entry per bar, same order as chartData: the start of each bar's period, local time. */
  bucketDates: Date[] = [];

  /**
   * Bar names are ISO dates so they stay unique; this turns them back into a tick. Bound as an
   * arrow property because ngx-charts calls it detached from the component.
   */
  tickFormat = (name: any): string => this.toTick(name);

  // ---- Compare -------------------------------------------------------------------------------

  /**
   * Which period a point belongs to: 0 for the one the dialog calls left, 1 for right.
   *
   * Decided by the dates that were asked for, not by the dates that came back. The two used to be the
   * earliest and latest dates in the response, so when one period had no readings (a meter installed
   * after it) the other period was taken for both: Sep 2026's figure was shown as Sep 2024's, with
   * "-100%" against an empty right-hand side.
   */
  private compareSide(date: unknown): 0 | 1 {
    const t = new Date(date as any).getTime();
    const parse = (v: string) => new Date((v || '').replace(' ', 'T')).getTime();
    const a = parse(this.compareLeft || this.dateLeft), b = parse(this.compareRight || this.dateRight);
    if (isNaN(a) || isNaN(b)) return 0;
    return a <= b ? (t >= b ? 1 : 0) : (t >= a ? 0 : 1);
  }

  /**
   * A period's name as its dates, "1 Jul 2026 to 31 Jul 2026", from the proc's own label
   * ("01.07.2026 - 31.07.2026"), which is what the SSRS report titles its pies with.
   */
  private static periodName(label: string | null | undefined): string {
    const m = /^(\d{2})\.(\d{2})\.(\d{4})\s*-\s*(\d{2})\.(\d{2})\.(\d{4})$/.exec((label ?? '').trim());
    if (!m) return label ?? '';
    const fmt = (d: string, mo: string, y: string) =>
      formatDate(new Date(Number(y), Number(mo) - 1, Number(d)), { day: 'numeric', month: 'short', year: 'numeric' });
    return `${fmt(m[1], m[2], m[3])} to ${fmt(m[4], m[5], m[6])}`;
  }

  /**
   * The same dates as briefly as they read, for the axis: "11 to 17 May 2026", "31 Aug to 6 Sept
   * 2026". Two full names ran into each other under a window half a screen wide (15/10/26).
   */
  private static periodTick(label: string | null | undefined): string {
    const m = /^(\d{2})\.(\d{2})\.(\d{4})\s*-\s*(\d{2})\.(\d{2})\.(\d{4})$/.exec((label ?? '').trim());
    if (!m || m[3] !== m[6]) return ResourceSeriesChartComponent.periodName(label);
    const day = (d: string, mo: string, y: string, opts: Intl.DateTimeFormatOptions) =>
      formatDate(new Date(Number(y), Number(mo) - 1, Number(d)), opts);
    const end = day(m[4], m[5], m[6], { day: 'numeric', month: 'short', year: 'numeric' });
    if (m[2] !== m[5]) return `${day(m[1], m[2], m[3], { day: 'numeric', month: 'short' })} to ${end}`;
    return m[1] === m[4] ? end : `${Number(m[1])} to ${end}`;
  }

  /** The proc's label for the period a bucket key stands for. */
  private compareRaw(key: any): string | null {
    for (const s of this.rawSeries) {
      for (const p of s.points ?? []) {
        if (bucketKey(p.date as any, this.periodKey) === String(key)) return p.label ?? '';
      }
    }
    return null;
  }

  /** The period a bucket key stands for, named by its dates. */
  private compareLabel(key: any): string | null {
    const raw = this.compareRaw(key);
    return raw === null ? null : ResourceSeriesChartComponent.periodName(raw);
  }

  /**
   * A figure of this chart's unit as kg of CO2, or null where the unit is not a mass of CO2 or the
   * figure is an average. Production's carbon policies chart in "kg of CO2" (187 sites) or "Tonnes
   * CO2" (170 sites); the SSRS card only knew kg, so a tonnes chart never had one.
   */
  private kgCo2(value: number | null | undefined): number | null {
    const unit = (this.unit || '').toLowerCase().replace(/\s+/g, ' ').trim();
    // CO2 by name, with or without the subscript or the "e". Carbon alone is not CO2: a kg of carbon
    // is 3.67 kg of CO2, so it is left without a card rather than given one 3.67 times too small.
    const co2 = /co[2₂]/.test(unit);
    if (!co2 || this.averaged || value == null || value <= 0 || unit.includes('/')) return null;
    if (/^(?:t|tonnes?|tons?|metric tons?)\b|^t ?co/.test(unit)) return value * 1000;
    return /^(?:kg|kilo)/.test(unit) ? value : null;
  }

  /**
   * How many mature trees would take a year to absorb this much CO2, at 22 kg each, as Anjali's tree
   * card on the SSRS carbon report worked it out. Null where kgCo2 is.
   */
  treesFor(value: number | null | undefined): number | null {
    const kg = this.kgCo2(value);
    return kg === null ? null : Math.ceil(kg / ResourceSeriesChartComponent.KG_CO2_PER_TREE_YEAR);
  }

  /**
   * Anjali's tree card, on the totals strip so it is there whether the totals are open or folded:
   * the trees a year that would absorb the CO2 on screen. It follows the chart, so drilling into a
   * month gives that month's answer, which is what the card gave for a clicked month, and the
   * whole window is answered too. Not under Compare, whose tiles carry both periods' trees.
   */
  get treeCard(): { trees: number; how: string } | null {
    if (this.compare || !this.totals) return null;
    const kg = this.kgCo2(this.totals.total);
    if (kg === null) return null;
    const trees = Math.ceil(kg / ResourceSeriesChartComponent.KG_CO2_PER_TREE_YEAR);
    const n = (v: number, places = 0) => formatNumber(v, places);
    return {
      trees,
      how: `Estimated tree equivalent: ${n(kg)} kg CO₂ ÷ 22 = ${n(kg / 22, 2)} → ${n(trees)} mature ${trees === 1 ? 'tree' : 'trees'}. `
        + 'Estimated using 22 kg CO₂ absorbed per mature tree per year. Actual absorption varies by species, '
        + 'age, location, and tree maturity.'
    };
  }

  /** What one mature tree absorbs in a year, as the SSRS card used. Varies by species, age and place. */
  static readonly KG_CO2_PER_TREE_YEAR = 22;

  /**
   * The two periods' names, for the pie titles and the tiles, left then right. A period with no
   * readings is named by the date it was asked for, and says so, as the SSRS pie said "No Data".
   */
  get comparePeriods(): string[] {
    return this.compareCached().periods;
  }

  private valueAt(s: ResourceSeriesVM, side: 0 | 1): number {
    return (s.points ?? []).filter(p => this.compareSide(p.date) === side).reduce((a, p) => a + (p.value ?? 0), 0);
  }

  /**
   * Everything Compare draws, worked out once per response. As getters they were rebuilt on every
   * change-detection pass, and the pies, handed a new array each time, redrew on every mouse move.
   */
  private compareCached(): CompareView {
    if (this.compareCacheFor === this.rawSeries && this.compareCache) return this.compareCache;

    const names: (string | null)[] = [null, null];
    for (const s of this.rawSeries) {
      for (const p of s.points ?? []) {
        const side = this.compareSide(p.date);
        if (names[side] === null) names[side] = ResourceSeriesChartComponent.periodName(p.label);
      }
    }
    const asked = [this.compareLeft || this.dateLeft, this.compareRight || this.dateRight].map(v => {
      const d = new Date((v || '').replace(' ', 'T'));
      return isNaN(d.getTime()) ? '' : formatDate(d, { day: 'numeric', month: 'short', year: 'numeric' });
    });
    const periods = names.map((n, i) => n ?? asked[i] + ' (no data)');

    const change = (l: number, r: number) => (l !== 0 ? ((r - l) / Math.abs(l)) * 100 : null);
    const rows: CompareRow[] = this.rawSeries.map(s => {
      const left = this.valueAt(s, 0), right = this.valueAt(s, 1);
      return { name: s.name ?? '', rgb: s.rgb || '#999999', left, right, change: change(left, right), total: false };
    });
    if (!this.averaged && rows.length > 1) {
      const left = rows.reduce((x, r) => x + r.left, 0);
      const right = rows.reduce((x, r) => x + r.right, 0);
      rows.push({ name: 'Total', rgb: '#6b7280', left, right, change: change(left, right), total: true });
    }

    // A negative figure has no share of a whole, so it is left out of its pie.
    const pies = ([0, 1] as (0 | 1)[]).map(side => this.rawSeries
      .map(s => ({ name: s.name ?? '', value: this.valueAt(s, side) }))
      .filter(x => x.value > 0));

    const colours = this.rawSeries.map(s => ({ name: s.name ?? '', value: s.rgb || '#999999' }));

    const legends = ([0, 1] as (0 | 1)[]).map(side => rows.filter(r => !r.total).map(r => ({
      name: r.name, rgb: r.rgb, value: formatWithUnit(side === 0 ? r.left : r.right, this.unit, this.reportKind, this.averaged)
    })));

    this.compareCache = { periods, rows, pies, colours, legends };
    this.compareCacheFor = this.rawSeries;
    return this.compareCache;
  }
  private compareCache: CompareView | null = null;
  private compareCacheFor: ResourceSeriesVM[] | null = null;

  /**
   * One row per group, and a total where adding up means something: each period's figure and the
   * change from the first to the second. The change is null when the first period had nothing,
   * since a percentage of nothing is not a number anyone can use.
   */
  get compareRows(): CompareRow[] {
    return this.compareCached().rows;
  }

  /** Each period's shares, for its pie. */
  get comparePies(): { name: string; value: number }[][] {
    return this.compareCached().pies;
  }

  /** Each group keeps its own colour in the pies, the same as in the bars. */
  get pieColours(): { name: string; value: string }[] {
    return this.compareCached().colours;
  }

  /** Each period's legend under its pie: every group's figure, as SSRS listed them beside its pies. */
  get compareLegends(): { name: string; rgb: string; value: string }[][] {
    return this.compareCached().legends;
  }

  byRowName = (_: number, row: { name: string }) => row.name;

  /** The column beside Compare's bars, measured with them (see measureChart). */
  private compareColumn: [number, number] = [240, 300];

  /**
   * Each pie takes half the column's height, less its title and its legend: a row of 18px for each
   * group, up to the six the legend shows before it scrolls (.rsc-compare-legend).
   */
  get pieView(): [number, number] {
    const [w, h] = this.compareColumn;
    const rows = Math.min(6, this.compareLegends[0]?.length ?? 0);
    const legend = rows ? 4 + rows * 18 : 0;
    const side = Math.max(100, Math.min(w, Math.floor(h / 2) - 24 - legend));
    return [w, side];
  }

  /**
   * Short axis ticks: "Mon 6", not the proc's "Mon 06.07.26". The month is not repeated on every
   * tick because it is written once under the axis - same split the SSRS report uses.
   */
  /** True once the window covers more than one month, which changes what a tick has to say. */
  spansMonths = false;

  /**
   * 'h' | 'd' | 'w' | 'm' | 'q' | 'y' - one bar is one of these.
   *
   * Follows the drill once you are inside it, so ticks, keys and month brackets all describe what
   * is actually on screen rather than the period the dialog was opened on.
   */
  private get periodKey(): Period {
    const top = this.drillStack[this.drillStack.length - 1];
    if (top) return top.level;
    const p = (this.period || 'd').slice(-1).toLowerCase();
    return (['h', 'd', 'w', 'm', 'q', 'y'].includes(p) ? p : 'd') as Period;
  }

  /** Only day and week bars need month brackets; a month bar already names its own month. */
  get useMonthBands(): boolean {
    if (this.compare) return false;
    return this.spansMonths && (this.periodKey === 'd' || this.periodKey === 'w');
  }

  private toTick(date: any): string {
    // A compare bar is a whole period, named by its dates.
    if (this.compare) {
      const raw = this.compareRaw(date);
      return raw === null ? String(date ?? '') : ResourceSeriesChartComponent.periodTick(raw);
    }

    // An hour key carries its own tick already, and splitting it is simpler than re-parsing.
    const hourKey = typeof date === 'string' && /^\d{4}-\d{2}-\d{2} (\d{2}):00$/.exec(date);
    if (hourKey) return hourKey[1] + ':00';

    // A bare "YYYY-MM-DD" is parsed as UTC by the Date constructor, which lands on the previous
    // day west of Greenwich. Split it instead.
    const iso = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date) ? date.split('-') : null;
    const d = iso
      ? new Date(Number(iso[0]), Number(iso[1]) - 1, Number(iso[2]))
      : new Date(date);
    if (isNaN(d.getTime())) return String(date ?? '');

    // A month, quarter or year bar names itself - a bare "1" or "31" says nothing.
    if (this.periodKey === 'y') return String(d.getFullYear());
    // By its number, not the month it starts in: "Apr 2026" made you work out it meant Q2.
    if (this.periodKey === 'q') return `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
    if (this.periodKey === 'm') return formatDate(d, { month: 'short', year: 'numeric' });

    // A week bar is named by the Monday it starts on, with the month where that is not obvious.
    if (this.periodKey === 'w') {
      return this.spansMonths ? formatDate(d, { day: 'numeric', month: 'short' }) : String(d.getDate());
    }

    // Under month brackets the day number is enough; when there are too many, axisTicks writes
    // every k-th one rather than none.
    if (this.useMonthBands) return String(d.getDate());

    return formatDate(d, { weekday: 'short', day: 'numeric' });
  }

  private countMonths(series: ResourceSeriesVM[]): number {
    const months = new Set<string>();
    for (const s of series) {
      for (const p of s.points ?? []) {
        const d = new Date(p.date as any);
        if (!isNaN(d.getTime())) months.add(d.getFullYear() + '-' + d.getMonth());
      }
    }
    return months.size;
  }

  private toPeriodLabel(series: ResourceSeriesVM[]): string {
    // No flatMap: the app's TS target predates es2019.
    const dates: Date[] = [];
    for (const s of series) {
      for (const p of s.points ?? []) {
        const d = new Date(p.date as any);
        if (!isNaN(d.getTime())) dates.push(d);
      }
    }
    dates.sort((a: Date, b: Date) => a.getTime() - b.getTime());

    if (!dates.length) return '';

    const fmt = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
    const first = fmt(dates[0]);
    const last = fmt(dates[dates.length - 1]);
    return first === last ? first : `${first} - ${last}`;
  }

  /**
   * The tiles' figures, by the rules of the SSRS report this chart replaces (see totals.ts).
   *
   * @param metered The series are the response's own, so their server figures describe them. False
   *   for a scenario's series, which are copies with the points rewritten and still carry the
   *   response's gross and window figures, now wrong for them.
   */
  private toTotals(series: ResourceSeriesVM[], metered: boolean): ResourceTotals {
    return computeTotals(series, {
      kind: this.reportKind,
      averaged: this.averaged,
      grid: this.grid(series),
      metered
    });
  }

  /**
   * Re-split a SCENARIO's import and export from the hourly readings. The metered split comes from
   * the server (see toTotals); this is only for what-if series, whose hours the server never saw.
   * It belongs with the what-if, and goes there once the scenario carries its own split.
   *
   * A bucket value is already netted off. An August that imported 90 MWh and exported 9 MWh arrives
   * as one positive number, so summing buckets reports 81 MWh imported and no export at all - which
   * is exactly what the tiles were showing on a Month view. Every figure that depends on the split
   * is wrong with it: import is understated, export reads zero, and self-consumption picks up the
   * whole of generation.
   *
   * The split only exists at the interval the meter recorded, so it is recomputed from the hourly
   * series the battery already loads. Generation and the per-group tiles are unaffected by
   * bucketing and are left exactly as they were.
   */
  private withHourlySplit(totals: ResourceTotals, k: number): ResourceTotals {
    // No hourly data, or no grid/generation split to correct in the first place.
    if (!this.hourlyLoaded || this.hourlyRows.length < 2) return totals;
    if (totals.grossImport === null) return totals;

    // The hourly join drops any interval one series has and the other does not, so it can cover a
    // shorter window than the chart. Where the two disagree, leave the bucketed figures alone
    // rather than quietly reporting a different window's totals. Site load is unchanged by the
    // multiplier - the grid absorbs it - so this comparison holds at any k.
    const bucketed = totals.perSeries.reduce((a, x) => a + x.value, 0);
    const metered = this.hourlyRows.reduce((a, h) => a + h.gridKwh + h.solarKwh, 0);
    if (Math.abs(bucketed) > 1 && Math.abs(metered - bucketed) / Math.abs(bucketed) > 0.01) return totals;

    // The same cap the chart applied, so the tiles and the bars cannot disagree.
    const cap = this.liveCap;

    let grossImport = 0;
    let grossExport = 0;
    let generated = 0;

    for (const h of this.hourlyRows) {
      const load = h.gridKwh + h.solarKwh;
      // Plus the estimated array on a site with no solar meter, which the hourly rows carry per kWp.
      const added = this.scenarioFresh ? this.scenarioAddedKwp : 0;
      const raw = k * h.solarKwh + added * (h.kwpKwh ?? 0);
      const solar = cap !== null && raw > cap ? cap : raw;
      const grid = load - solar;

      if (grid > 0) grossImport += grid;
      else grossExport -= grid;

      generated += solar;
    }

    const selfConsumed = generated - grossExport;
    const site = grossImport + selfConsumed;

    return {
      ...totals,
      grossImport,
      grossExport,
      netMains: grossImport - grossExport,
      generated,
      selfConsumed,
      siteConsumption: site,
      total: site
    };
  }
}
